import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { SpinnerComponent, ToastComponent } from '@mzic/mzic-components';
import { LanguageService } from '@mzic/mzic-services';
import {
  catchError,
  defer,
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  forkJoin,
  from,
  interval,
  map,
  merge,
  Observable,
  of,
  repeat,
  Subject,
  switchMap,
  tap,
  take,
  takeUntil,
  takeWhile,
  timer,
} from 'rxjs';
import {
  AuthenticatingOverlayComponent,
  AuthenticatingStatus,
} from '../components/authenticating-overlay/authenticating-overlay.component';
import { LongWaitDialogComponent } from '../components/long-wait-dialog/long-wait-dialog.component';
import { LoadingSkeletonComponent } from '../components/loading-skeleton/loading-skeleton.component';
import { CancelConnectionDialogComponent } from '../components/cancel-connection-dialog/cancel-connection-dialog.component';
import { ResumeConnectionDialogComponent } from '../components/resume-connection-dialog/resume-connection-dialog.component';
import { ShareInfoDialogComponent } from '../components/share-info-dialog/share-info-dialog.component';
import { SkipConfirmationDialogComponent } from '../components/skip-confirmation-dialog/skip-confirmation-dialog.component';
import { CredentialsStepComponent } from '../components/steps/credentials-step/credentials-step.component';
import { CompletedConnectionStepComponent } from '../components/steps/completed-connection-step/completed-connection-step.component';
import { EntitySelectionStepComponent } from '../components/steps/entity-selection-step/entity-selection-step.component';
import { FileUploadStepComponent } from '../components/steps/file-upload-step/file-upload-step.component';
import { FileSelectionDiscardDialogComponent } from '../components/file-selection-discard-dialog/file-selection-discard-dialog.component';
import { FileSetConflictDialogComponent } from '../components/file-set-conflict-dialog/file-set-conflict-dialog.component';
import { FileUpdateConfirmationDialogComponent } from '../components/file-update-confirmation-dialog/file-update-confirmation-dialog.component';
import {
  FileValidationIssue,
  FileValidationIssuesDialogComponent,
} from '../components/file-validation-issues-dialog/file-validation-issues-dialog.component';
import {
  FileRequiredInputsDialogComponent,
  FileRequiredInputsReviewItem,
  FileRequiredInputsSubmission,
} from '../components/file-required-inputs-dialog/file-required-inputs-dialog.component';
import { IntroStepComponent } from '../components/steps/intro-step/intro-step.component';
import { PasscodeStepComponent } from '../components/steps/passcode-step/passcode-step.component';
import { PendingSharesStepComponent } from '../components/steps/pending-shares-step/pending-shares-step.component';
import { ResultStepComponent } from '../components/steps/result-step/result-step.component';
import { AuthInteractionStepComponent } from '../components/steps/auth-interaction-step/auth-interaction-step.component';
import { ProviderSetupRequiredStepComponent } from '../components/steps/provider-setup-required-step/provider-setup-required-step.component';
import {
  CollectionTargetLoadingMean,
  TargetRouteLoadingStepComponent,
} from '../components/steps/target-route-loading-step/target-route-loading-step.component';
import { TargetSelectionDialogComponent } from '../components/target-selection-dialog/target-selection-dialog.component';
import {
  BusinessRepresentativeSelection,
  BusinessRepSelectionComponent,
} from '../components/waiver-flow/business-rep-selection/business-rep-selection.component';
import {
  WaiverDialogComponent,
  WaiverSigningState,
} from '../components/waiver-flow/waiver-dialog/waiver-dialog.component';
import {
  WaiverTypeSelectionComponent,
  WaiverTypeSelectionResult,
} from '../components/waiver-flow/waiver-type-selection/waiver-type-selection.component';
import {
  AuthInteraction,
  AuthInteractionChannel,
  AuthInteractionCodeFormat,
  AuthInteractionEndedReason,
  AuthInteractionKind,
  AuthInteractionLastError,
  BusinessEntity,
  BusinessRepresentative,
  CollectionEnvelopeDto,
  ConnectionStatus,
  ConnectionEntity,
  EntityType,
  RevisionShareTargetResponse,
  RevisionTargetShare,
  SubmitAuthInteractionRequest,
  VerifyPasscodeError,
  WaiverDocument,
} from '../interfaces/collection.interface';
import { AuthService } from '../services/auth.service';
import { CollectionService } from '../services/collection.service';
import { CollectionRevisionService } from '../services/collection-revision.service';
import { CollectionRevisionConnectionService } from '../services/collection-revision-connection.service';
import {
  CollectionRevisionAuthInteractionDetailDto,
  CollectionRevisionAuthInteractionIdentity,
  CollectionRevisionConnectionTargetDto,
  ConnectionFailureDto,
  ConnectionEntityDto,
} from '../interfaces/collection-revision-connection.interface';
import {
  EntityOwnerWaiverDto,
  EntityOwnerWaiverDocumentDto,
  GenerateEntityOwnerWaiverBatchRequest,
} from '../interfaces/collection-revision-waiver.interface';
import { CollectionWorkflowDto } from '../interfaces/collection-revision.interface';
import {
  isStatementOriginValidationErrorCode,
  StatementOriginValidationErrorCode,
} from '../interfaces/statement-origin.interface';
import {
  AuthInteractionDraftState,
  AuthInteractionPersistenceService,
} from '../services/auth-interaction-persistence.service';
import { WaiverService } from '../services/waiver.service';
import { buildErrorMessage, parseApiError } from '../utils/error-parser';
import { CollectionRequestIdempotencyService } from '../idempotency/collection-request-idempotency.service';
import { CollectionWebLocksUnavailableError } from '../session/collection-web-lock';
import {
  CredentialsMode,
  getFailurePresentationFromSummary,
  getNextRevisionTargetIntent,
  getPreviousRevisionTargetIntent,
} from './target-runtime';
import {
  buildCollectionRevisionTargetRoute,
  CollectionRevisionTargetRouteIdentity,
  parseCollectionRevisionTargetRoute,
} from './collection-revision-route';
import {
  CollectionRevisionFileEditorCommitResult,
  CollectionRevisionFileEditorController,
  CollectionRevisionFileValidationError,
} from './collection-revision-file-editor.controller';
import {
  CollectionRevisionFileValidationSnapshot,
  CollectionRevisionNavigationDirection,
} from './collection-revision-file-editor';
import {
  CollectionTargetRouteConflictError,
  CollectionTargetRouteContractError,
  CollectionTargetRouteLoader,
  FreshWorkflowHandoff,
  LoadedTargetBundle,
} from './collection-target-route-loader';
import {
  CollectionConnectionPresenter,
  ConnectionPresentationOrigin,
  ConnectionScreenDecision,
  PROVIDER_SETUP_REQUIRED_CODE,
} from './collection-connection-presenter';
import { CollectionRevisionLifecycleCoordinator } from './collection-revision-lifecycle-coordinator';

type FlowStep =
  | 'loading'
  | 'forced-entity-error'
  | 'passcode'
  | 'intro'
  | 'entity-selection'
  | 'credentials'
  | 'auth-interaction'
  | 'auth-interaction-ended'
  | 'provider-setup-required'
  | 'connection-completed'
  | 'file-upload'
  | 'result'
  | 'error'
  | 'pending-shares';

type ForcedResolverState = 'idle' | 'loading' | 'failed';

const ACTIVE_CONNECTION_POLL_INTERVAL_MS = 10_000;
const PUSH_APPROVAL_CHECK_INTERVAL_MS = 4_000;
/** Consecutive failed push checks before the flow stops and offers resume. */
const PUSH_APPROVAL_MAX_FAILED_CHECKS = 3;

interface PendingAuthInteractionState {
  entity: ConnectionEntity | null;
  connectionStatus: ConnectionStatus;
  authInteraction: AuthInteraction;
  exactIdentity: CollectionRevisionAuthInteractionIdentity;
  draftState: AuthInteractionDraftState;
}

type ConnectionDraftContext =
  | { readonly kind: 'UNSELECTED' }
  | {
      readonly kind: 'READY';
      readonly entity: ConnectionEntity;
      readonly waiver: EntityOwnerWaiverDto;
    };

interface PendingConnectIntent {
  readonly route: CollectionRevisionTargetRouteIdentity;
  readonly connectionId: string;
  readonly entityId: string;
  readonly credentials: Readonly<Record<string, string>>;
}

interface CollectionRouteState {
  readonly collectionId: string;
  readonly identity: CollectionRevisionTargetRouteIdentity | null;
}

interface CollectionTargetRouteState extends CollectionRouteState {
  readonly releaseNavigationBarrier: boolean;
  readonly forceReload: boolean;
}

interface CollectionTargetRouteReplay {
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly releaseNavigationBarrier: boolean;
}

type LongWaitDialogState = 'hidden' | 'visible' | 'acknowledged';

type CollectionRouteLoadErrorKind =
  | 'CONFLICT'
  | 'NOT_FOUND'
  | 'TRANSIENT'
  | 'CONTRACT';

interface CollectionBootstrapLoadError {
  readonly scope: 'COLLECTION';
  readonly collectionId: string;
  readonly kind: Exclude<CollectionRouteLoadErrorKind, 'CONFLICT'>;
}

interface TargetRouteLoadError {
  readonly scope: 'TARGET';
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly kind: CollectionRouteLoadErrorKind;
  readonly message: string;
}

type CollectionRouteLoadError =
  | CollectionBootstrapLoadError
  | TargetRouteLoadError;

interface TargetEntryTransition {
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly mean: CollectionTargetLoadingMean;
}

interface ConnectionPollError {
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly kind: 'TRANSIENT' | 'UNAUTHORIZED';
  readonly message: string;
}

interface CompletedConnectionEntityLoadIntent {
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly entityId: string;
  readonly requestKey: string;
}

@Component({
  selector: 'lib-collection-flow',
  imports: [
    CommonModule,
    TranslocoModule,
    SpinnerComponent,
    ToastComponent,
    LoadingSkeletonComponent,
    TargetRouteLoadingStepComponent,
    AuthenticatingOverlayComponent,
    LongWaitDialogComponent,
    IntroStepComponent,
    PasscodeStepComponent,
    EntitySelectionStepComponent,
    CredentialsStepComponent,
    CompletedConnectionStepComponent,
    AuthInteractionStepComponent,
    ProviderSetupRequiredStepComponent,
    FileUploadStepComponent,
    FileSelectionDiscardDialogComponent,
    FileSetConflictDialogComponent,
    FileUpdateConfirmationDialogComponent,
    FileValidationIssuesDialogComponent,
    FileRequiredInputsDialogComponent,
    ResultStepComponent,
    PendingSharesStepComponent,
    ResumeConnectionDialogComponent,
    CancelConnectionDialogComponent,
    ShareInfoDialogComponent,
    SkipConfirmationDialogComponent,
    TargetSelectionDialogComponent,
    // Waiver flow components
    WaiverTypeSelectionComponent,
    BusinessRepSelectionComponent,
    WaiverDialogComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './collection-flow.component.html',
  styleUrl: './collection-flow.component.scss',
})
export class CollectionFlowComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly authService = inject(AuthService);
  private readonly collectionService = inject(CollectionService);
  private readonly collectionRevisionService = inject(
    CollectionRevisionService,
  );
  private readonly revisionFileEditor = inject(
    CollectionRevisionFileEditorController,
  );
  private readonly targetRouteLoader = inject(CollectionTargetRouteLoader);
  private readonly connectionService = inject(
    CollectionRevisionConnectionService,
  );
  private readonly connectionPresenter = new CollectionConnectionPresenter();
  private readonly lifecycleCoordinator = inject(
    CollectionRevisionLifecycleCoordinator,
  );
  private readonly collectionRequestIdempotency = inject(
    CollectionRequestIdempotencyService,
  );
  private readonly authInteractionPersistence = inject(
    AuthInteractionPersistenceService,
  );
  private readonly waiverService = inject(WaiverService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translocoService = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  @ViewChild(CredentialsStepComponent)
  credentialsStep?: CredentialsStepComponent;
  @ViewChild(FileUploadStepComponent)
  fileUploadStep?: FileUploadStepComponent;
  @ViewChild(PendingSharesStepComponent)
  pendingSharesStep?: PendingSharesStepComponent;

  currentStep = signal<FlowStep>('loading');
  collection = signal<CollectionEnvelopeDto | null>(null);
  readonly loadedTargetBundle = signal<LoadedTargetBundle | null>(null);
  readonly loadedWorkflow = computed(
    () => this.loadedTargetBundle()?.workflow ?? null,
  );
  readonly loadedTarget = computed(
    () => this.loadedTargetBundle()?.target ?? null,
  );
  readonly loadedRuntime = computed(
    () => this.loadedTargetBundle()?.runtime ?? null,
  );
  readonly workflow = this.loadedWorkflow;
  readonly bootstrapping = signal(false);
  readonly transitioningToTarget =
    signal<CollectionRevisionTargetRouteIdentity | null>(null);
  readonly targetEntryTransition = signal<TargetEntryTransition | null>(null);
  readonly routeLoadError = signal<CollectionRouteLoadError | null>(null);
  readonly collectionBootstrapError = computed(() => {
    const error = this.routeLoadError();
    return error?.scope === 'COLLECTION' ? error : null;
  });
  readonly targetRouteLoadError = computed(() => {
    const error = this.routeLoadError();
    return error?.scope === 'TARGET' ? error : null;
  });
  readonly fileEditorState = this.revisionFileEditor.state;
  readonly showFileValidationIssuesDialog = signal(false);
  readonly showFileRequiredInputsDialog = signal(false);
  readonly fileRequiredInputsReviewQueue = signal<readonly string[]>([]);
  readonly fileRequiredInputsReviewIndex = signal(0);
  readonly fileRequiredInputsReviewBusy = signal(false);
  readonly fileRequiredInputsReviewError = signal('');
  readonly fileValidationIssues = computed<readonly FileValidationIssue[]>(
    () => {
      const state = this.fileEditorState();
      return Object.values(state.validationByFile).flatMap((validation) => {
        if (validation.usable && validation.required_inputs.length === 0) {
          return [];
        }
        const row = state.rows.find(
          (candidate) =>
            this.revisionFileEditor.fileIdForRow(candidate) ===
            validation.file_id,
        );
        return [
          {
            fileId: validation.file_id,
            fileName:
              row?.name ||
              (row?.origin === 'PERSISTED' ? row.fileId : row?.file.name) ||
              validation.file_id,
            sizeBytes: row?.sizeBytes ?? null,
            kind:
              validation.required_inputs.length > 0
                ? ('RESOLVABLE' as const)
                : ('PERMANENT' as const),
            reason: this.fileValidationIssueReason(validation),
          },
        ];
      });
    },
  );
  readonly presentedFileValidationIssues = computed<
    readonly FileValidationIssue[]
  >(() => {
    const issues = this.fileValidationIssues();
    const permanent = issues.filter((issue) => issue.kind === 'PERMANENT');
    return permanent.length > 0
      ? permanent
      : issues.filter((issue) => issue.kind === 'RESOLVABLE');
  });
  readonly currentFileRequiredInputsReview =
    computed<FileRequiredInputsReviewItem | null>(() => {
      const fileId =
        this.fileRequiredInputsReviewQueue()[
          this.fileRequiredInputsReviewIndex()
        ];
      if (!fileId) return null;
      const state = this.fileEditorState();
      const requiredInputs = state.requiredInputsByFile[fileId] ?? [];
      if (requiredInputs.length === 0) return null;
      const row = state.rows.find(
        (candidate) =>
          this.revisionFileEditor.fileIdForRow(candidate) === fileId,
      );
      return {
        fileId,
        fileName:
          row?.name ||
          (row?.origin === 'PERSISTED' ? row.fileId : row?.file.name) ||
          fileId,
        sizeBytes: row?.sizeBytes ?? null,
        requiredInputs,
        resolvedInputs: state.resolvedInputsByFile[fileId] ?? {},
      };
    });
  readonly exactConnectionRuntime =
    signal<CollectionRevisionConnectionTargetDto | null>(null);
  readonly connectionScreen = signal<ConnectionScreenDecision | null>(null);
  readonly completedConnectionAcquisition = computed(() => {
    const screen = this.connectionScreen();
    return screen?.kind === 'COMPLETED_READ_ONLY' ? screen.acquisition : null;
  });
  /** Kind of the prompt that just ended; the ended connection no longer says. */
  private readonly endedInteractionKind = signal<AuthInteractionKind | null>(
    null,
  );
  /** Last prompt kind a runtime listed in this session, per connection. */
  private lastPromptKind: {
    readonly connectionId: string;
    readonly kind: AuthInteractionKind;
  } | null = null;
  readonly endedAuthInteraction = computed(() => {
    const screen = this.connectionScreen();
    const entity = this.exactConnectionRuntime()?.connection?.entity;
    if (screen?.kind !== 'AUTH_INTERACTION_ENDED' || !entity) return null;
    const kind = endedTwoFactorKind(screen.reason, this.endedInteractionKind());
    return {
      reason: screen.reason,
      view: toEndedTwoFactorView(entity.auth_capabilities, kind),
    };
  });
  readonly completedConnectionEntity = signal<ConnectionEntity | null>(null);
  readonly completedConnectionEntityLoading = signal(false);
  readonly connectionCommandBusy = signal(false);
  readonly connectionPollError = signal<ConnectionPollError | null>(null);

  // Target management
  currentTargetId = signal<string | null>(null);
  private activeRevisionIdentity: CollectionRevisionTargetRouteIdentity | null =
    null;
  private targetEntryRequiresIntro = true;
  private collectionOwnershipGeneration = 0;
  private readonly authenticatedCollection$ = new Subject<{
    collectionId: string;
    generation: number;
  }>();
  private readonly stopCollectionOwnership$ = new Subject<void>();
  private revisionOperationInFlight = false;
  private pendingBrowserRouteIdentity: CollectionRevisionTargetRouteIdentity | null =
    null;
  private pendingExternalNavigationUrl: string | null = null;
  private authorizedNavigationUrl: string | null = null;
  private canonicalWorkflowRouteHandoff:
    | (FreshWorkflowHandoff & {
        readonly generation: number;
      })
    | null = null;
  private readonly retryTargetRoute$ =
    new Subject<CollectionTargetRouteReplay>();
  private readonly retryCollectionBootstrap$ = new Subject<string>();
  private routeErrorBackIdentity: CollectionRevisionTargetRouteIdentity | null =
    null;
  private activeTargetRouteIntent: CollectionRevisionTargetRouteIdentity | null =
    null;

  // Current flow state
  selectedEntity = signal<ConnectionEntity | null>(null);
  currentConnectionId = signal<string | null>(null);
  connectionDraft = signal<ConnectionDraftContext>({ kind: 'UNSELECTED' });
  entityOwnerWaiver = signal<EntityOwnerWaiverDto | null>(null);
  private pendingConnectIntent: PendingConnectIntent | null = null;
  pendingAuthInteractionState = signal<PendingAuthInteractionState | null>(
    null,
  );
  /** A push check is waiting for its answer; a resend waits for it. */
  readonly pushCheckInFlight = signal(false);
  private resendAfterPushCheck = false;
  /** The push prompt on screen while it can still be approved. */
  private readonly pushApprovalPrompt$ = toObservable(
    computed(() => {
      const state = this.pendingAuthInteractionState();
      return this.currentStep() === 'auth-interaction' &&
        state?.exactIdentity.kind === 'PUSH_APPROVAL'
        ? state.exactIdentity
        : null;
    }),
  );

  // Loading states
  authenticating = signal(false);
  authenticatingStatus = signal<AuthenticatingStatus>('CONNECTING');
  longWaitDialogState = signal<LongWaitDialogState>('hidden');
  completingCollection = signal(false);

  // Minimum display time for authenticating overlay (4 seconds)
  private readonly AUTHENTICATING_MIN_DISPLAY_MS = 4000;
  private readonly AUTHENTICATING_ERROR_TRANSITION_MS = 1200;
  private readonly AUTHENTICATING_LONG_WAIT_DIALOG_MS = 30000;
  private readonly COMPLETE_REDIRECT_DELAY_SECONDS = 5;
  private readonly PASSCODE_QUERY_PARAM = 'passcode';
  private readonly PASSCODE_QUERY_PATTERN = /^\d{6}$/;
  private authenticatingStartTime: number | null = null;
  private longWaitDialogTimeoutId: number | null = null;
  private documentScrollLocked = false;
  private originalBodyOverflow = '';
  private originalHtmlOverflow = '';
  private redirectIntervalId: number | null = null;
  private redirectTimeoutId: number | null = null;
  private redirectScheduled = false;
  private queryPasscodeAttempted = false;
  private integrationEntityNameValue = signal('');

  // Result state
  isSuccess = signal(false);
  resultMessage = signal('');
  totalFiles = signal(0);
  errorDetails = signal<string | undefined>(undefined);
  private completedRevisionFileTotalGeneration = 0;

  // Passcode state
  passcodeError = signal<string | null>(null);
  passcodeAttempts = signal(0);
  passcodeBackoffSeconds = signal(0);

  // Polling control
  private readonly exactConnectionPollIntent$ =
    new Subject<CollectionRevisionTargetRouteIdentity | null>();
  private readonly completedConnectionEntityLoadIntent$ =
    new Subject<CompletedConnectionEntityLoadIntent | null>();
  private completedConnectionEntityRequestKey: string | null = null;
  private waiverRequiredConnectionRequestKey: string | null = null;
  private waiverRequiredConnectionResolvedKey: string | null = null;

  // Share feature state
  isOwner = signal(false);
  showTargetSelectionDialog = signal(false);
  showShareInfoDialog = signal(false);
  showSkipConfirmationDialog = signal(false);
  shareResponse = signal<RevisionShareTargetResponse | null>(null);
  targetShares = signal<readonly RevisionTargetShare[]>([]);
  pendingSharesLoading = signal(false);
  shareSubmitting = signal(false);
  toastVisible = signal(false);
  toastMessage = signal('');
  toastState = signal<'error' | 'success' | 'alert' | 'info'>('error');
  skipButtonHidden = signal(false);

  // Waiver flow state
  individualId = signal<string | null>(null);
  businessId = signal<string | null>(null);
  entityType = signal<EntityType | null>(null);
  requiresWaiver = signal(false);
  waiverDocuments = signal<WaiverDocument[]>([]);
  waiverSigningState = signal<WaiverSigningState>('idle');
  waiverLoading = signal(false);
  isRightsholder = signal(false);
  representativeCpf = signal<string | null>(null);
  businessEntity = signal<BusinessEntity | null>(null);
  businessRepresentatives = signal<BusinessRepresentative[]>([]);
  businessRepsLoading = signal(false);
  selectedBusinessRep = signal<BusinessRepresentative | null>(null);

  // Waiver dialog visibility signals
  showWaiverTypeDialog = signal(false);
  showBusinessRepDialog = signal(false);
  showWaiverSigningDialog = signal(false);
  waiversSigned = signal(false); // Track if waivers have been successfully signed for current entity

  credentialsMode = signal<CredentialsMode>('initial');
  currentTargetFailure = signal<ConnectionFailureDto | null>(null);
  authInteractionBannerVariant = signal<'reauth-auth-interaction' | null>(null);
  authInteractionLoading = signal(false);
  authInteractionPageLoading = signal(false);
  authInteractionResending = signal(false);
  authInteractionError = signal<string | null>(null);

  // Resume connection flow state
  showResumeDialog = signal(false);
  showCancelConfirmDialog = signal(false);
  resumeDialogLoading = signal(false);
  cancelDialogLoading = signal(false);
  requiresWaiverResign = signal(false);
  isForcedTarget = signal(false);
  forcedResolverState = signal<ForcedResolverState>('idle');
  forcedResolverErrorMessage = signal<string | null>(null);
  private forcedResolverRequestId = 0;

  collectionId = '';

  constructor() {
    this.startCompletedConnectionEntityResolution();

    effect(
      () => {
        if (this.authenticating()) {
          this.lockDocumentScroll();
        } else {
          this.restoreDocumentScroll();
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const status = this.authenticatingStatus();
        const isAuthenticating = this.authenticating();

        if (
          this.longWaitDialogState() === 'visible' &&
          (!isAuthenticating ||
            (status !== 'CONNECTING' && status !== 'IN_PROGRESS'))
        ) {
          this.longWaitDialogState.set('hidden');
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.setLanguageFromRoute();
    const initialCollectionId =
      this.route.snapshot.paramMap.get('collectionId')?.trim() ?? '';

    this.destroyRef.onDestroy(() => {
      this.clearRedirectTimers();
      this.resetLongWaitDialogState();
      this.restoreDocumentScroll();
    });

    if (!initialCollectionId) {
      this.currentStep.set('error');
      this.resultMessage.set(this.getInvalidCollectionMessage());
      return;
    }

    this.startExactConnectionPolling();
    this.startPushApprovalChecks();

    // Subscribe to token expiration events to route to passcode step
    this.authService.tokenExpired$
      .pipe(
        filter((id) => id === this.collectionId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.exactConnectionPollIntent$.next(null);
        this.clearPendingAuthInteractionState();
        this.clearRedirectTimers();
        this.hideAuthenticating();
        this.currentStep.set('passcode');
      });

    this.startCollectionOwnershipFlow();
  }

  private startCollectionOwnershipFlow(): void {
    const collectionRouteState$ = this.route.paramMap.pipe(
      map(
        (params): CollectionRouteState => ({
          collectionId: params.get('collectionId')?.trim() ?? '',
          identity: parseCollectionRevisionTargetRoute({
            collectionId: params.get('collectionId'),
            revisionId: params.get('revisionId'),
            targetId: params.get('targetId'),
          }),
        }),
      ),
      filter(({ collectionId }) => collectionId.length > 0),
      distinctUntilChanged(
        (left, right) => left.collectionId === right.collectionId,
      ),
    );
    const retryCollectionState$ = this.retryCollectionBootstrap$.pipe(
      map(
        (collectionId): CollectionRouteState => ({
          collectionId,
          identity: null,
        }),
      ),
    );

    merge(collectionRouteState$, retryCollectionState$)
      .pipe(
        switchMap(({ collectionId }) => {
          const previousCollectionId = this.collectionId;
          const generation = ++this.collectionOwnershipGeneration;
          if (previousCollectionId && previousCollectionId !== collectionId) {
            this.resetCollectionOwnership(previousCollectionId);
          }

          this.collectionId = collectionId;
          this.routeLoadError.set(null);
          this.clearErrorToast();
          this.currentStep.set('loading');
          return this.resolveCollectionAuthentication(
            collectionId,
            generation,
          ).pipe(
            switchMap(() =>
              this.startAuthenticatedRouteFlow(collectionId, generation),
            ),
            catchError((error) => {
              if (this.isActiveCollectionOwnership(collectionId, generation)) {
                this.publishCollectionBootstrapError(collectionId, error);
              }
              return of(undefined);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private resolveCollectionAuthentication(
    collectionId: string,
    generation: number,
  ): Observable<void> {
    return this.authService.hasUsableSession(collectionId).pipe(
      take(1),
      switchMap((hasUsableSession) => {
        if (!this.isActiveCollectionOwnership(collectionId, generation)) {
          return of(undefined);
        }
        if (hasUsableSession) {
          return of(undefined);
        }

        const queryPasscode = this.getPasscodeFromQuery();
        if (
          queryPasscode &&
          this.PASSCODE_QUERY_PATTERN.test(queryPasscode) &&
          !this.queryPasscodeAttempted
        ) {
          return new Observable<void>((subscriber) => {
            const subscription = this.authenticatedCollection$
              .pipe(
                filter(
                  (authenticated) =>
                    authenticated.collectionId === collectionId &&
                    authenticated.generation === generation,
                ),
                map(() => undefined),
              )
              .subscribe(subscriber);
            this.queryPasscodeAttempted = true;
            this.submitPasscode(queryPasscode, true);
            return () => subscription.unsubscribe();
          });
        }

        this.currentStep.set('passcode');
        return this.authenticatedCollection$.pipe(
          filter(
            (authenticated) =>
              authenticated.collectionId === collectionId &&
              authenticated.generation === generation,
          ),
          map(() => undefined),
        );
      }),
      takeUntil(this.stopCollectionOwnership$),
    );
  }

  private getPasscodeFromQuery(): string | null {
    const value = this.route.snapshot.queryParamMap.get(
      this.PASSCODE_QUERY_PARAM,
    );
    if (!value) return null;
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  private setLanguageFromRoute(): void {
    const parentParams$ = this.route.parent?.params;

    if (parentParams$) {
      parentParams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (params) => {
          const lang = params['lang'];
          const normalized = this.normalizeLang(lang);
          this.translocoService.setActiveLang(normalized);
          this.languageService.languageState.set(normalized);
        },
      });
      return;
    }

    const fallback = this.normalizeLang(
      this.languageService.getBrowserLanguage(),
    );
    this.translocoService.setActiveLang(fallback);
    this.languageService.languageState.set(fallback);
  }

  private normalizeLang(lang: string | null | undefined): 'pt-br' | 'en' {
    if (lang?.toLowerCase().startsWith('pt')) {
      return 'pt-br';
    }
    return 'en';
  }

  /**
   * Preloads images and returns a promise that resolves when all images are loaded.
   * Includes a timeout to prevent indefinite waiting.
   */
  private preloadImages(imageUrls: string[]): Promise<void> {
    const TIMEOUT_MS = 5000; // 5 seconds timeout

    const loadImage = (url: string): Promise<void> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => {
          console.warn(`Failed to preload image: ${url}`);
          resolve(); // Resolve anyway to not block the flow
        };
        img.src = url;
      });
    };

    const loadAllImages = Promise.all(imageUrls.map(loadImage)).then(
      () => undefined,
    );

    const timeout = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn('Image preloading timed out');
        resolve();
      }, TIMEOUT_MS);
    });

    // Race between loading all images and timeout
    return Promise.race([loadAllImages, timeout]);
  }

  /**
   * Gets the list of all image URLs used in the intro step
   */
  private getIntroStepImageUrls(partnerIconUrl: string | null): string[] {
    const imageUrls: string[] = [
      'assets/icons/mzic-link-icon.svg',
      'assets/icons/mzic-logo-purple.svg',
      'assets/icons/mzic-check-mark-filled.svg',
      'assets/icons/mzic-lock.svg',
    ];

    // Add partner icon if it exists (no fallback)
    if (partnerIconUrl) {
      imageUrls.push(partnerIconUrl);
    }

    return imageUrls;
  }

  private persistPendingAuthInteractionState(
    state: PendingAuthInteractionState,
  ): void {
    const { exactIdentity } = state;
    const base = {
      collectionId: exactIdentity.collectionId,
      revisionId: exactIdentity.revisionId,
      targetId: exactIdentity.targetId,
      connectionId: exactIdentity.connectionId,
      challengeId: exactIdentity.challengeId,
      generation: exactIdentity.generation,
      updatedAt: new Date().toISOString(),
    };
    if (exactIdentity.kind === 'SCOPE_SELECTION') {
      this.authInteractionPersistence.saveDraft({
        ...base,
        kind: 'SCOPE_SELECTION',
        page: state.draftState.page ?? 1,
        selectedScopeIds: state.draftState.selected_ids ?? [],
      });
    } else {
      this.authInteractionPersistence.saveDraft({
        ...base,
        kind: exactIdentity.kind,
      });
    }
  }

  private clearPendingAuthInteractionState(
    identity = this.pendingAuthInteractionState()?.exactIdentity,
  ): void {
    if (identity) {
      this.authInteractionPersistence.clearDraft(identity);
      this.collectionRequestIdempotency.clearSubmitAuthInteractionIntents(
        identity.collectionId,
        identity.connectionId,
      );
    }

    this.pendingAuthInteractionState.set(null);
    this.authInteractionLoading.set(false);
    this.authInteractionPageLoading.set(false);
    this.authInteractionResending.set(false);
    this.resendAfterPushCheck = false;
    this.authInteractionError.set(null);
  }

  private applyCollectionEnvelopeState(envelope: CollectionEnvelopeDto): void {
    this.collection.set(envelope);
    this.isOwner.set(envelope.is_owner);
    this.individualId.set(envelope.individual_id);
    this.businessId.set(envelope.business_id);
    this.entityType.set(envelope.entity_type);
  }

  private isActiveCollectionOwnership(
    collectionId: string,
    generation: number,
  ): boolean {
    return (
      this.collectionId === collectionId &&
      this.collectionOwnershipGeneration === generation
    );
  }

  private resetCollectionOwnership(collectionId: string): void {
    this.stopCollectionOwnership$.next();
    this.clearRedirectTimers();
    this.resetLongWaitDialogState();
    this.restoreDocumentScroll();
    this.collectionRequestIdempotency.clearCollectionIntents(collectionId);
    this.authInteractionPersistence.clearCollection(collectionId);
    this.revisionFileEditor.reset();
    this.showFileValidationIssuesDialog.set(false);
    this.clearCompletedConnectionEntityPresentation();

    this.collection.set(null);
    this.loadedTargetBundle.set(null);
    this.bootstrapping.set(false);
    this.transitioningToTarget.set(null);
    this.targetEntryTransition.set(null);
    this.routeLoadError.set(null);
    this.connectionPollError.set(null);
    this.routeErrorBackIdentity = null;
    this.isOwner.set(false);
    this.individualId.set(null);
    this.businessId.set(null);
    this.entityType.set(null);
    this.activeRevisionIdentity = null;
    this.targetEntryRequiresIntro = true;
    this.activeTargetRouteIntent = null;
    this.currentTargetId.set(null);
    this.canonicalWorkflowRouteHandoff = null;
    this.pendingBrowserRouteIdentity = null;
    this.pendingExternalNavigationUrl = null;
    this.authorizedNavigationUrl = null;
    this.revisionOperationInFlight = false;

    this.selectedEntity.set(null);
    this.currentConnectionId.set(null);
    this.connectionDraft.set({ kind: 'UNSELECTED' });
    this.entityOwnerWaiver.set(null);
    this.pendingConnectIntent = null;
    this.pendingAuthInteractionState.set(null);
    this.authInteractionBannerVariant.set(null);
    this.authInteractionLoading.set(false);
    this.authInteractionPageLoading.set(false);
    this.authInteractionResending.set(false);
    this.authInteractionError.set(null);

    this.showResumeDialog.set(false);
    this.showCancelConfirmDialog.set(false);
    this.showTargetSelectionDialog.set(false);
    this.showShareInfoDialog.set(false);
    this.showSkipConfirmationDialog.set(false);
    this.resumeDialogLoading.set(false);
    this.cancelDialogLoading.set(false);
    this.shareResponse.set(null);
    this.targetShares.set([]);
    this.pendingSharesLoading.set(false);
    this.shareSubmitting.set(false);
    this.businessRepsLoading.set(false);

    this.authenticating.set(false);
    this.authenticatingStartTime = null;
    this.authenticatingStatus.set('CONNECTING');
    this.completingCollection.set(false);
    this.passcodeError.set(null);
    this.passcodeAttempts.set(0);
    this.passcodeBackoffSeconds.set(0);
    this.queryPasscodeAttempted = false;

    this.isSuccess.set(false);
    this.resultMessage.set('');
    this.totalFiles.set(0);
    this.errorDetails.set(undefined);
    this.toastVisible.set(false);
    this.toastMessage.set('');
    this.skipButtonHidden.set(false);

    this.credentialsMode.set('initial');
    this.currentTargetFailure.set(null);
    this.requiresWaiverResign.set(false);
    this.waiversSigned.set(false);
    this.clearWaiverState();

    this.integrationEntityNameValue.set('');
    this.currentStep.set('loading');
  }

  private getFailurePresentation(failure: ConnectionFailureDto | null): {
    message: string;
    code: string | null;
    occurredAt: string | null;
  } {
    return getFailurePresentationFromSummary(failure, this.activeLang);
  }

  private startAuthenticatedRouteFlow(
    collectionId: string,
    generation: number,
  ): Observable<void> {
    this.clearRedirectTimers();
    this.bootstrapping.set(true);
    this.currentStep.set('loading');

    const routeState$ = this.route.paramMap.pipe(
      map(
        (params): CollectionTargetRouteState => ({
          collectionId: params.get('collectionId')?.trim() ?? '',
          identity: parseCollectionRevisionTargetRoute({
            collectionId: params.get('collectionId'),
            revisionId: params.get('revisionId'),
            targetId: params.get('targetId'),
          }),
          releaseNavigationBarrier: this.revisionOperationInFlight,
          forceReload: false,
        }),
      ),
      filter((routeState) => routeState.collectionId === collectionId),
      distinctUntilChanged(
        (left, right) =>
          left.collectionId === right.collectionId &&
          left.identity?.revisionId === right.identity?.revisionId &&
          left.identity?.targetId === right.identity?.targetId,
      ),
    );
    const retryState$ = this.retryTargetRoute$.pipe(
      filter(({ identity }) => identity.collectionId === collectionId),
      map(
        ({
          identity,
          releaseNavigationBarrier,
        }): CollectionTargetRouteState => ({
          collectionId,
          identity,
          releaseNavigationBarrier,
          forceReload: true,
        }),
      ),
    );

    return this.collectionService.getCollectionEnvelope(collectionId).pipe(
      tap((envelope) => {
        if (this.isActiveCollectionOwnership(collectionId, generation)) {
          this.applyCollectionEnvelopeState(envelope);
        }
      }),
      catchError((error) => {
        if (this.isActiveCollectionOwnership(collectionId, generation)) {
          this.publishCollectionBootstrapError(collectionId, error);
        }
        return EMPTY;
      }),
      switchMap(() => merge(routeState$, retryState$)),
      filter(() => this.isActiveCollectionOwnership(collectionId, generation)),
      switchMap(({ identity, releaseNavigationBarrier, forceReload }) => {
        if (!identity) {
          return this.replaceCollectionEntryWithWorkflowRoute(
            collectionId,
            generation,
          ).pipe(
            catchError((error) => {
              if (this.isActiveCollectionOwnership(collectionId, generation)) {
                this.publishCollectionBootstrapError(collectionId, error);
              }
              return EMPTY;
            }),
          );
        }
        return this.resolveRevisionRouteEmission(
          identity,
          generation,
          forceReload,
        ).pipe(
          tap(() => {
            if (releaseNavigationBarrier) {
              this.releaseRevisionNavigationBarrier();
            }
          }),
          catchError((error) => {
            if (this.isActiveCollectionOwnership(collectionId, generation)) {
              if (releaseNavigationBarrier) {
                this.releaseRevisionNavigationBarrier();
              }
              this.publishTargetRouteError(identity, error);
            }
            return EMPTY;
          }),
        );
      }),
      takeUntil(this.stopCollectionOwnership$),
    );
  }

  private resolveRevisionRouteEmission(
    identity: CollectionRevisionTargetRouteIdentity,
    generation = this.collectionOwnershipGeneration,
    forceReload = false,
  ): Observable<void> {
    if (forceReload) {
      return this.loadRevisionRoute(identity, generation);
    }
    const active = this.activeRevisionIdentity;
    if (!active || this.revisionOperationInFlight) {
      return this.loadRevisionRoute(identity, generation);
    }
    if (sameRevisionTargetIdentity(active, identity)) {
      return of(undefined);
    }
    if (this.fileEditorState().busy) {
      void this.navigateToRevisionTarget(active, true);
      return of(undefined);
    }
    if (!this.fileEditorState().dirty) {
      return this.loadRevisionRoute(identity, generation);
    }

    const direction = this.resolveBrowserRouteDirection(active, identity);
    const decision = this.revisionFileEditor.requestNavigation(direction);
    if (
      decision.kind === 'CONFIRM_UPDATE' ||
      decision.kind === 'CONFIRM_DISCARD_SELECTION'
    ) {
      this.pendingBrowserRouteIdentity = identity;
      return of(undefined);
    }
    if (decision.kind === 'NAVIGATE') {
      return this.loadRevisionRoute(identity, generation);
    }
    void this.navigateToRevisionTarget(active, true);
    return of(undefined);
  }

  private resolveBrowserRouteDirection(
    active: CollectionRevisionTargetRouteIdentity,
    destination: CollectionRevisionTargetRouteIdentity,
  ): CollectionRevisionNavigationDirection {
    const ordered = [...(this.workflow()?.targets ?? [])].sort(
      (left, right) => left.order_idx_snapshot - right.order_idx_snapshot,
    );
    const activeIndex = ordered.findIndex(
      (target) => target.target_id === active.targetId,
    );
    const destinationIndex = ordered.findIndex(
      (target) => target.target_id === destination.targetId,
    );
    return destinationIndex >= 0 && destinationIndex < activeIndex
      ? 'BACK'
      : 'NEXT';
  }

  private loadRevisionRoute(
    identity: CollectionRevisionTargetRouteIdentity,
    generation = this.collectionOwnershipGeneration,
  ): Observable<void> {
    return defer(() => {
      if (
        !this.isActiveCollectionOwnership(identity.collectionId, generation)
      ) {
        return of(undefined);
      }
      this.activeTargetRouteIntent = Object.freeze({ ...identity });
      this.beginTargetRouteLoad(identity);
      const handoff = this.consumeCanonicalWorkflowRouteHandoff(
        identity,
        generation,
      );
      return this.targetRouteLoader.load(identity, handoff ?? undefined).pipe(
        tap((bundle) =>
          this.applyLoadedTargetBundle(bundle, identity, generation),
        ),
        map(() => undefined),
        finalize(() => this.finishTargetRouteLoad(identity, generation)),
      );
    });
  }

  private beginTargetRouteLoad(
    identity: CollectionRevisionTargetRouteIdentity,
  ): void {
    this.exactConnectionPollIntent$.next(null);
    this.clearCompletedConnectionEntityPresentation();
    this.routeLoadError.set(null);
    const currentBundle = this.loadedTargetBundle();
    this.prepareKnownTargetEntryTransition(
      identity,
      currentBundle?.workflow ?? null,
    );
    if (
      currentBundle &&
      currentBundle.workflow.revision_id !== identity.revisionId
    ) {
      this.clearRevisionShareState();
    }
    const destinationExistsInCurrentWorkflow =
      currentBundle?.workflow.targets.some(
        (target) => target.target_id === identity.targetId,
      );
    if (currentBundle && destinationExistsInCurrentWorkflow) {
      const previous = getPreviousRevisionTargetIntent(
        currentBundle.workflow.targets,
        identity.targetId,
      );
      this.routeErrorBackIdentity =
        previous && 'targetId' in previous
          ? {
              collectionId: identity.collectionId,
              revisionId: identity.revisionId,
              targetId: previous.targetId,
            }
          : null;
    } else {
      this.routeErrorBackIdentity = null;
    }
    const targetEntryTransition = this.targetEntryTransition();
    const preservesTargetEntryBaseline =
      targetEntryTransition !== null &&
      sameRevisionTargetIdentity(targetEntryTransition.identity, identity);
    if (!preservesTargetEntryBaseline) {
      this.targetEntryTransition.set(null);
      this.loadedTargetBundle.set(null);
      this.exactConnectionRuntime.set(null);
      this.connectionScreen.set(null);
      this.revisionFileEditor.reset();
    }
    if (this.activeRevisionIdentity) {
      this.transitioningToTarget.set(identity);
    } else {
      this.bootstrapping.set(true);
    }
  }

  private applyLoadedTargetBundle(
    bundle: LoadedTargetBundle,
    expectedIdentity: CollectionRevisionTargetRouteIdentity,
    generation: number,
  ): void {
    if (
      !this.isActiveCollectionOwnership(
        expectedIdentity.collectionId,
        generation,
      ) ||
      !this.activeTargetRouteIntent ||
      !sameRevisionTargetIdentity(
        this.activeTargetRouteIntent,
        expectedIdentity,
      ) ||
      !sameRevisionTargetIdentity(bundle.identity, expectedIdentity)
    ) {
      return;
    }

    this.loadedTargetBundle.set(bundle);
    this.activeRevisionIdentity = bundle.identity;
    this.currentTargetId.set(bundle.identity.targetId);
    this.routeLoadError.set(null);

    if (this.targetEntryRequiresIntro) {
      this.prepareIntroBeforeTarget();
      return;
    }

    this.presentLoadedTargetBundle(bundle);
    const targetEntryTransition = this.targetEntryTransition();
    if (
      targetEntryTransition &&
      sameRevisionTargetIdentity(
        targetEntryTransition.identity,
        bundle.identity,
      )
    ) {
      this.targetEntryTransition.set(null);
    }
  }

  private presentLoadedTargetBundle(bundle: LoadedTargetBundle): void {
    if (
      bundle.target.target_status === 'COMPLETED' &&
      isRevisionLifecycleTerminal(bundle.workflow) &&
      bundle.workflow.targets.every(
        (target) => target.target_status === 'COMPLETED',
      )
    ) {
      this.clearCompletedConnectionEntityPresentation();
      this.finishTerminalIntegrationWorkflow(bundle.workflow)
        .pipe(
          takeUntil(this.stopCollectionOwnership$),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          error: (error) =>
            this.publishConnectionProgressFailure(bundle.identity, error),
        });
      return;
    }

    if (bundle.runtime.mean === 'UPLOAD') {
      this.clearCompletedConnectionEntityPresentation();
      this.showFileValidationIssuesDialog.set(false);
      this.revisionFileEditor.hydrate(
        bundle.identity,
        bundle.runtime.fileSet,
        this.isIntegrationEnabled,
      );
      this.currentStep.set('file-upload');
      return;
    }

    this.presentExactConnectionRuntime(
      bundle.runtime.connectionTarget,
      'ROUTE_HYDRATION',
    );
  }

  private prepareIntroBeforeTarget(): void {
    this.exactConnectionPollIntent$.next(null);
    this.revisionFileEditor.reset();
    this.exactConnectionRuntime.set(null);
    this.connectionScreen.set(null);
    this.connectionPollError.set(null);
    this.selectedEntity.set(null);
    this.currentConnectionId.set(null);
    this.connectionDraft.set({ kind: 'UNSELECTED' });
    this.entityOwnerWaiver.set(null);
    this.pendingConnectIntent = null;
    this.currentTargetFailure.set(null);
    this.showResumeDialog.set(false);
    this.showCancelConfirmDialog.set(false);
    this.showWaiverTypeDialog.set(false);
    this.showBusinessRepDialog.set(false);
    this.showWaiverSigningDialog.set(false);
    this.currentStep.set('intro');
  }

  private presentExactConnectionRuntime(
    runtime: CollectionRevisionConnectionTargetDto,
    origin: ConnectionPresentationOrigin,
    onPublished?: (decision: ConnectionScreenDecision) => void,
  ): void {
    if (runtime.target_status === 'COMPLETED') {
      this.connectionDraft.set({ kind: 'UNSELECTED' });
      this.clearConnectionWaiverRequirement();
      this.publishExactConnectionRuntime(runtime, origin);
      this.loadCompletedConnectionEntity(runtime);
      const decision = this.connectionScreen();
      if (decision) onPublished?.(decision);
      return;
    }

    this.clearCompletedConnectionEntityPresentation();

    const entityDto = runtime.connection?.entity ?? runtime.configured_entity;
    if (!entityDto) {
      this.connectionDraft.set({ kind: 'UNSELECTED' });
      this.clearConnectionWaiverRequirement();
      this.publishExactConnectionRuntime(runtime, origin);
      const decision = this.connectionScreen();
      if (decision) onPublished?.(decision);
      return;
    }

    const entity = toConnectionEntityView(entityDto);
    this.selectedEntity.set(entity);
    if (runtime.connection !== null) {
      this.connectionDraft.set({ kind: 'UNSELECTED' });
      const waiverRequirementKey = this.getWaiverRequirementKey(runtime);
      this.publishExactConnectionRuntime(runtime, origin);
      const decision = this.connectionScreen();
      if (decision) onPublished?.(decision);
      if (waiverRequirementKey) {
        const resolvedWaiver = this.entityOwnerWaiver();
        if (
          this.waiverRequiredConnectionResolvedKey === waiverRequirementKey &&
          resolvedWaiver
        ) {
          this.openWaiverGate(resolvedWaiver);
        } else {
          this.loadWaiverRequiredByConnection(
            runtime,
            entity,
            waiverRequirementKey,
          );
        }
        return;
      }
      this.clearConnectionWaiverRequirement();
      return;
    }

    this.clearConnectionWaiverRequirement();
    this.loadExactEntityOwnerWaiver(runtime, entity, origin, onPublished);
  }

  private getWaiverRequirementKey(
    runtime: CollectionRevisionConnectionTargetDto,
  ): string | null {
    const connection = runtime.connection;
    const errorCodes = [
      connection?.last_error?.code,
      connection?.latest_failure?.error.code,
    ];
    if (!errorCodes.includes('WAIVER_AUTHORIZATION_REQUIRED') || !connection) {
      return null;
    }

    return [
      runtime.collection_id,
      runtime.revision_id,
      runtime.target_id,
      connection.connection_id,
    ].join(':');
  }

  private loadWaiverRequiredByConnection(
    runtime: CollectionRevisionConnectionTargetDto,
    entity: ConnectionEntity,
    requestKey: string,
  ): void {
    if (this.waiverRequiredConnectionRequestKey === requestKey) return;

    const identity = {
      collectionId: runtime.collection_id,
      revisionId: runtime.revision_id,
      targetId: runtime.target_id,
    } as const;
    this.waiverRequiredConnectionRequestKey = requestKey;
    this.waiverService
      .getEntityOwnerWaiver(identity, entity.id)
      .pipe(
        take(1),
        finalize(() => {
          if (this.waiverRequiredConnectionRequestKey === requestKey) {
            this.waiverRequiredConnectionRequestKey = null;
          }
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (waiver) => {
          const currentRuntime = this.exactConnectionRuntime();
          if (
            !currentRuntime ||
            this.getWaiverRequirementKey(currentRuntime) !== requestKey ||
            this.selectedEntity()?.id !== entity.id
          ) {
            return;
          }
          this.entityOwnerWaiver.set(waiver);
          this.waiverRequiredConnectionResolvedKey = requestKey;
          this.openWaiverGate(waiver);
        },
        error: (error) =>
          this.handleApiError(error, this.getUnexpectedErrorMessage()),
      });
  }

  private clearConnectionWaiverRequirement(): void {
    this.waiverRequiredConnectionRequestKey = null;
    this.waiverRequiredConnectionResolvedKey = null;
    this.entityOwnerWaiver.set(null);
  }

  private startCompletedConnectionEntityResolution(): void {
    this.completedConnectionEntityLoadIntent$
      .pipe(
        switchMap((intent) => {
          if (!intent) {
            return EMPTY;
          }

          return this.collectionService
            .getConnectionEntityById(
              intent.entityId,
              intent.identity.collectionId,
            )
            .pipe(
              take(1),
              map((entity) => ({ intent, entity })),
              catchError(() => of({ intent, entity: null })),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ intent, entity }) => {
        if (!this.isCurrentCompletedConnectionEntityIntent(intent)) {
          return;
        }

        this.completedConnectionEntity.set(entity);
        this.completedConnectionEntityLoading.set(false);
      });
  }

  private loadCompletedConnectionEntity(
    runtime: CollectionRevisionConnectionTargetDto,
  ): void {
    const entityId = runtime.acquisition?.entity.entity_id?.trim() ?? '';
    const identity = {
      collectionId: runtime.collection_id,
      revisionId: runtime.revision_id,
      targetId: runtime.target_id,
    } as const;
    const requestKey = [
      identity.collectionId,
      identity.revisionId,
      identity.targetId,
      entityId || 'missing-entity',
    ].join(':');

    if (this.completedConnectionEntityRequestKey === requestKey) {
      return;
    }

    this.completedConnectionEntityRequestKey = requestKey;
    this.completedConnectionEntity.set(null);

    if (!entityId) {
      this.completedConnectionEntityLoading.set(false);
      this.completedConnectionEntityLoadIntent$.next(null);
      return;
    }

    this.completedConnectionEntityLoading.set(true);
    this.completedConnectionEntityLoadIntent$.next({
      identity,
      entityId,
      requestKey,
    });
  }

  private isCurrentCompletedConnectionEntityIntent(
    intent: CompletedConnectionEntityLoadIntent,
  ): boolean {
    const active = this.getRevisionRouteIdentity();
    const runtime = this.exactConnectionRuntime();
    return (
      this.completedConnectionEntityRequestKey === intent.requestKey &&
      active !== null &&
      sameRevisionTargetIdentity(active, intent.identity) &&
      runtime !== null &&
      runtime.target_status === 'COMPLETED' &&
      runtime.acquisition?.entity.entity_id === intent.entityId
    );
  }

  private clearCompletedConnectionEntityPresentation(): void {
    if (
      this.completedConnectionEntityRequestKey === null &&
      this.completedConnectionEntity() === null &&
      !this.completedConnectionEntityLoading()
    ) {
      return;
    }

    this.completedConnectionEntityRequestKey = null;
    this.completedConnectionEntity.set(null);
    this.completedConnectionEntityLoading.set(false);
    this.completedConnectionEntityLoadIntent$.next(null);
  }

  private loadExactEntityOwnerWaiver(
    runtime: CollectionRevisionConnectionTargetDto,
    entity: ConnectionEntity,
    origin: ConnectionPresentationOrigin,
    onPublished?: (decision: ConnectionScreenDecision) => void,
  ): void {
    const identity = {
      collectionId: runtime.collection_id,
      revisionId: runtime.revision_id,
      targetId: runtime.target_id,
    } as const;
    this.connectionCommandBusy.set(true);
    this.waiverService
      .getEntityOwnerWaiver(identity, entity.id)
      .pipe(
        take(1),
        finalize(() => this.connectionCommandBusy.set(false)),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (waiver) => {
          const currentRoute = this.getRevisionRouteIdentity();
          if (
            !currentRoute ||
            !sameRevisionTargetIdentity(currentRoute, identity) ||
            this.selectedEntity()?.id !== entity.id
          ) {
            return;
          }
          this.entityOwnerWaiver.set(waiver);
          this.connectionDraft.set({ kind: 'READY', entity, waiver });
          this.publishExactConnectionRuntime(runtime, origin);
          const decision = this.connectionScreen();
          if (decision) onPublished?.(decision);
          if (runtime.connection === null) {
            this.credentialsMode.set('initial');
            this.currentStep.set('credentials');
          }
        },
        error: (error) => {
          this.handleApiError(error, this.getUnexpectedErrorMessage());
          if (this.selectedEntity()?.id === entity.id) {
            this.connectionDraft.set({ kind: 'UNSELECTED' });
            this.entityOwnerWaiver.set(null);
            this.currentStep.set(
              runtime.configured_entity ? 'credentials' : 'entity-selection',
            );
          }
        },
      });
  }

  private publishExactConnectionRuntime(
    runtime: CollectionRevisionConnectionTargetDto,
    origin: ConnectionPresentationOrigin,
  ): void {
    const decision = this.connectionPresenter.present(runtime, origin);
    const exactEntity = runtime.connection?.entity ?? runtime.configured_entity;
    if (this.isIntegrationEnabled && exactEntity) {
      this.integrationEntityNameValue.set(exactEntity.display_name);
    }
    const prompt = runtime.connection?.auth_interaction;
    if (runtime.connection && prompt) {
      this.lastPromptKind = {
        connectionId: runtime.connection.connection_id,
        kind: prompt.kind,
      };
    }
    this.exactConnectionRuntime.set(runtime);
    this.connectionScreen.set(decision);
    this.currentTargetFailure.set(
      decision.kind === 'CREDENTIALS' && decision.mode === 'FAILED_RETRY'
        ? (runtime.connection?.latest_failure ?? null)
        : null,
    );
    this.showResumeDialog.set(decision.kind === 'RESUME_OR_CANCEL');
    this.showWaiverTypeDialog.set(false);
    this.showBusinessRepDialog.set(false);
    this.showWaiverSigningDialog.set(false);

    switch (decision.kind) {
      case 'ENTITY_SELECTION':
        if (runtime.configured_entity) {
          this.credentialsMode.set('initial');
          this.currentStep.set('credentials');
          return;
        }
        this.currentStep.set('entity-selection');
        return;
      case 'CREDENTIALS':
        if (runtime.connection) {
          this.selectedEntity.set(
            toConnectionEntityView(runtime.connection.entity),
          );
          this.setCurrentConnectionId(runtime.connection.connection_id);
        }
        this.credentialsMode.set(
          decision.mode === 'FAILED_RETRY'
            ? 'failed_retry'
            : decision.mode === 'REAUTH'
              ? 'reauth'
              : 'initial',
        );
        this.currentStep.set('credentials');
        return;
      case 'RESUME_OR_CANCEL':
        // The dialog overlays the credentials presentation, matching the
        // existing collection-flow dialog shell without starting work.
        this.currentStep.set('credentials');
        return;
      case 'AUTH_INTERACTION':
        this.currentStep.set('auth-interaction');
        return;
      case 'AUTH_INTERACTION_ENDED':
      case 'PROVIDER_SETUP_REQUIRED':
        if (runtime.connection) {
          this.selectedEntity.set(
            toConnectionEntityView(runtime.connection.entity),
          );
          this.setCurrentConnectionId(runtime.connection.connection_id);
        }
        this.endedInteractionKind.set(
          this.lastPromptKind?.connectionId ===
            runtime.connection?.connection_id
            ? (this.lastPromptKind?.kind ?? null)
            : null,
        );
        this.clearPendingAuthInteractionState();
        this.currentStep.set(
          decision.kind === 'PROVIDER_SETUP_REQUIRED'
            ? 'provider-setup-required'
            : 'auth-interaction-ended',
        );
        return;
      case 'ACTIVE_PROGRESS':
        if (!this.authenticating()) this.showAuthenticating();
        this.authenticatingStatus.set('IN_PROGRESS');
        return;
      case 'COMPLETED_READ_ONLY':
        this.authenticatingStatus.set('COMPLETE');
        this.currentStep.set('connection-completed');
        return;
    }
  }

  /**
   * While a push prompt is on screen, ask the provider every few seconds
   * whether it was approved (one check at a time). An approval or a decline
   * changes the runtime and routes like a code answer; an expired prompt
   * re-reads the target; other failures toast once per failure streak.
   */
  private startPushApprovalChecks(): void {
    let failures = 0;
    this.pushApprovalPrompt$
      .pipe(
        // A re-read that re-activates the same prompt keeps cadence and streak.
        distinctUntilChanged(sameAuthInteractionPrompt),
        switchMap((identity) => {
          failures = 0;
          if (!identity) return EMPTY;
          return defer(() => this.checkPushApproval(identity)).pipe(
            repeat({ delay: PUSH_APPROVAL_CHECK_INTERVAL_MS }),
            takeUntil(this.stopCollectionOwnership$),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((check) => {
        const { identity } = check;
        // A result for a prompt the screen already left changes nothing.
        if (!this.isCurrentExactAuthIdentity(identity)) return;
        if ('error' in check) {
          if (isEndedAuthInteractionError(check.error)) {
            this.reloadExactAuthInteraction(identity, { ended: identity });
            return;
          }
          failures += 1;
          if (failures === 1) {
            this.showToast(this.getUnexpectedErrorMessage());
            this.reloadExactAuthInteraction(identity);
          } else if (failures >= PUSH_APPROVAL_MAX_FAILED_CHECKS) {
            // Stop asking; the user resumes or cancels from the dialog.
            this.presentAuthInteractionResume(this.exactConnectionRuntime());
          }
          return;
        }
        failures = 0;
        const connection = check.runtime.connection;
        const stillWaiting =
          connection?.status === 'AWAITING_AUTH_INTERACTION' &&
          connection.auth_interaction?.challenge_id === identity.challengeId &&
          connection.auth_interaction.generation === identity.generation;
        if (!stillWaiting) this.applyExactConnectionCommandRuntime(check.runtime);
      });
  }

  /**
   * One push check, or nothing when it must not run now: the prompt is no
   * longer current, a resend is in flight or the cancel dialog is open.
   */
  private checkPushApproval(
    identity: CollectionRevisionAuthInteractionIdentity,
  ) {
    if (
      !this.isCurrentExactAuthIdentity(identity) ||
      this.authInteractionResending() ||
      this.showCancelConfirmDialog()
    ) {
      return EMPTY;
    }
    // Every check is a new question: the backend stores a WAITING answer
    // under its Idempotency-Key, so a reused key would replay it.
    this.collectionRequestIdempotency.clearSubmitAuthInteractionIntents(
      identity.collectionId,
      identity.connectionId,
      'PUSH_APPROVAL',
    );
    this.pushCheckInFlight.set(true);
    return this.connectionService
      .submitAuthInteraction(identity, { payload: {} })
      .pipe(
        map((runtime) => ({ identity, runtime }) as const),
        catchError((error: unknown) => of({ identity, error })),
        finalize(() => this.settlePushApprovalCheck()),
      );
  }

  /** Sends a resend that was asked for while a push check was in flight. */
  private settlePushApprovalCheck(): void {
    this.pushCheckInFlight.set(false);
    if (!this.resendAfterPushCheck) return;
    this.resendAfterPushCheck = false;
    const state = this.pendingAuthInteractionState();
    if (state && this.isCurrentExactAuthIdentity(state.exactIdentity)) {
      this.resendExactAuthInteraction(state);
    } else {
      this.authInteractionResending.set(false);
    }
  }

  private startExactConnectionPolling(): void {
    this.exactConnectionPollIntent$
      .pipe(
        switchMap((identity) => {
          if (!identity) return EMPTY;
          const exactIdentity = Object.freeze({ ...identity });
          return timer(0, ACTIVE_CONNECTION_POLL_INTERVAL_MS).pipe(
            exhaustMap(() =>
              this.connectionService.getConnectionTarget(exactIdentity).pipe(
                map((runtime) => ({ identity: exactIdentity, runtime })),
                catchError((error) =>
                  of({ identity: exactIdentity, error } as const),
                ),
              ),
            ),
          );
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const { identity } = event;
        const active = this.getRevisionRouteIdentity();
        if (!active || !sameRevisionTargetIdentity(active, identity)) return;
        if ('error' in event) {
          this.publishConnectionProgressFailure(identity, event.error);
          return;
        }
        const { runtime } = event;
        this.connectionPollError.set(null);

        if (runtime.target_status === 'COMPLETED') {
          this.exactConnectionPollIntent$.next(null);
          this.presentExactConnectionRuntime(runtime, 'COMMAND_CONTINUATION');
          this.completeExactConnectionTarget(identity);
          return;
        }

        const bundle = this.loadedTargetBundle();
        if (
          bundle &&
          bundle.runtime.mean === 'CONNECTION' &&
          sameRevisionTargetIdentity(bundle.identity, identity)
        ) {
          this.loadedTargetBundle.set({
            ...bundle,
            runtime: { mean: 'CONNECTION', connectionTarget: runtime },
          });
        }
        this.presentExactConnectionRuntime(runtime, 'COMMAND_CONTINUATION');
      });
  }

  onConnectionPollRetry(): void {
    const failure = this.connectionPollError();
    const active = this.getRevisionRouteIdentity();
    if (
      !failure ||
      !active ||
      !sameRevisionTargetIdentity(failure.identity, active)
    ) {
      return;
    }
    this.connectionPollError.set(null);
    if (!this.authenticating()) this.showAuthenticating();
    this.authenticatingStatus.set('IN_PROGRESS');
    this.exactConnectionPollIntent$.next(active);
  }

  private publishConnectionProgressFailure(
    identity: CollectionRevisionTargetRouteIdentity,
    error: unknown,
  ): void {
    this.exactConnectionPollIntent$.next(null);
    this.hideAuthenticating(true);
    const unauthorized = isUnauthorizedRouteError(error);
    this.connectionPollError.set({
      identity: Object.freeze({ ...identity }),
      kind: unauthorized ? 'UNAUTHORIZED' : 'TRANSIENT',
      message: unauthorized
        ? this.getUnexpectedErrorMessage()
        : 'We could not confirm the latest connection status. Please try again.',
    });
    if (unauthorized) {
      this.handleApiError(error, this.getUnexpectedErrorMessage());
    }
  }

  private completeExactConnectionTarget(
    identity: CollectionRevisionTargetRouteIdentity,
  ): void {
    this.collectionRevisionService
      .getExactWorkflow(identity.collectionId, identity.revisionId)
      .pipe(
        switchMap((workflow) =>
          this.lifecycleCoordinator.complete(
            identity,
            workflow,
            workflow.etag,
            this.isIntegrationEnabled,
          ),
        ),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ workflow, authorization }) => {
          this.hideAuthenticating();
          const destinationTarget = [...workflow.targets]
            .sort(
              (left, right) =>
                left.order_idx_snapshot - right.order_idx_snapshot,
            )
            .find((target) => target.target_status !== 'COMPLETED');
          if (!destinationTarget) {
            if (!isRevisionLifecycleTerminal(workflow)) {
              this.showToast(this.getUnexpectedErrorMessage());
              return;
            }
            this.showRevisionComplete(workflow);
            if (authorization?.shouldRedirect) {
              this.startRedirectCountdown(authorization.url);
            }
            return;
          }
          const destination = {
            collectionId: workflow.collection_id,
            revisionId: workflow.revision_id,
            targetId: destinationTarget.target_id,
          };
          this.setCanonicalWorkflowRouteHandoff(destination, workflow);
          void this.navigateToRevisionTarget(destination);
        },
        error: (error) =>
          this.publishConnectionProgressFailure(identity, error),
      });
  }

  private finishTargetRouteLoad(
    identity: CollectionRevisionTargetRouteIdentity,
    generation: number,
  ): void {
    if (!this.isActiveCollectionOwnership(identity.collectionId, generation)) {
      return;
    }
    const transitioning = this.transitioningToTarget();
    if (transitioning && sameRevisionTargetIdentity(transitioning, identity)) {
      this.transitioningToTarget.set(null);
    }
    const loaded = this.loadedTargetBundle()?.identity;
    const failed = this.targetRouteLoadError()?.identity;
    if (
      (loaded && sameRevisionTargetIdentity(loaded, identity)) ||
      (failed && sameRevisionTargetIdentity(failed, identity))
    ) {
      this.bootstrapping.set(false);
    }
  }

  private publishTargetRouteError(
    identity: CollectionRevisionTargetRouteIdentity,
    error: unknown,
  ): void {
    if (isUnauthorizedRouteError(error)) {
      this.targetEntryTransition.set(null);
      this.bootstrapping.set(false);
      this.transitioningToTarget.set(null);
      this.handleApiError(error, this.getUnexpectedErrorMessage());
      return;
    }
    const status = (error as { status?: number })?.status;
    const kind: TargetRouteLoadError['kind'] =
      error instanceof CollectionTargetRouteConflictError
        ? 'CONFLICT'
        : error instanceof CollectionTargetRouteContractError
          ? 'CONTRACT'
          : status === 404
            ? 'NOT_FOUND'
            : 'TRANSIENT';
    const targetEntryTransition = this.targetEntryTransition();
    if (
      !targetEntryTransition ||
      !sameRevisionTargetIdentity(targetEntryTransition.identity, identity)
    ) {
      this.loadedTargetBundle.set(null);
    }
    this.bootstrapping.set(false);
    this.routeLoadError.set({
      scope: 'TARGET',
      identity: Object.freeze({ ...identity }),
      kind,
      message:
        status === 404
          ? 'This collection step is not available.'
          : 'We could not load this collection step. Please try again.',
    });
  }

  onTargetRouteRetry(): void {
    const error = this.targetRouteLoadError();
    if (!error || this.transitioningToTarget()) return;
    this.replayTargetRoute(error.identity);
  }

  onTargetRouteErrorBack(): void {
    const error = this.targetRouteLoadError();
    if (!error || this.transitioningToTarget()) return;
    const targetEntryTransition = this.targetEntryTransition();
    if (
      targetEntryTransition &&
      sameRevisionTargetIdentity(targetEntryTransition.identity, error.identity)
    ) {
      this.targetEntryRequiresIntro = true;
      this.targetEntryTransition.set(null);
      this.routeLoadError.set(null);
      this.prepareIntroBeforeTarget();
      return;
    }
    const destination = this.routeErrorBackIdentity;
    if (!destination) {
      void this.router.navigate(
        ['/', this.activeLang, 'collection', this.collectionId],
        {
          queryParams: this.route.snapshot.queryParams,
          fragment: this.route.snapshot.fragment || undefined,
        },
      );
      return;
    }
    void this.navigateToRevisionTarget(destination);
  }

  onCollectionBootstrapRetry(): void {
    const error = this.collectionBootstrapError();
    if (!error || this.bootstrapping()) return;
    this.retryCollectionBootstrap$.next(error.collectionId);
  }

  private publishCollectionBootstrapError(
    collectionId: string,
    error: unknown,
  ): void {
    this.bootstrapping.set(false);
    this.transitioningToTarget.set(null);
    this.targetEntryTransition.set(null);
    this.clearErrorToast();

    const status = (error as { readonly status?: number } | null)?.status;
    if (status === 401) {
      this.routeLoadError.set(null);
      this.exactConnectionPollIntent$.next(null);
      this.clearPendingAuthInteractionState();
      this.hideAuthenticating();
      this.currentStep.set('passcode');
      return;
    }

    const kind: CollectionBootstrapLoadError['kind'] =
      status === 403 || status === 404
        ? 'NOT_FOUND'
        : typeof status !== 'number' && error instanceof Error
          ? 'CONTRACT'
          : 'TRANSIENT';
    this.routeLoadError.set({
      scope: 'COLLECTION',
      collectionId,
      kind,
    });
  }

  private getRevisionRouteIdentity(): CollectionRevisionTargetRouteIdentity | null {
    return this.activeRevisionIdentity;
  }

  private finishTerminalIntegrationWorkflow(
    workflow: CollectionWorkflowDto,
  ): Observable<void> {
    const firstTarget = [...workflow.targets].sort(
      (left, right) => left.order_idx_snapshot - right.order_idx_snapshot,
    )[0];
    if (!firstTarget) {
      this.activeRevisionIdentity = null;
      this.currentTargetId.set(null);
      this.showRevisionComplete(workflow);
      return of(undefined);
    }
    const identity = {
      collectionId: workflow.collection_id,
      revisionId: workflow.revision_id,
      targetId: firstTarget.target_id,
    };
    return this.lifecycleCoordinator
      .complete(identity, workflow, workflow.etag, this.isIntegrationEnabled)
      .pipe(
        tap(({ workflow: nextWorkflow, authorization }) => {
          this.activeRevisionIdentity = null;
          this.currentTargetId.set(null);
          this.showRevisionComplete(nextWorkflow);
          if (authorization?.shouldRedirect) {
            this.startRedirectCountdown(authorization.url);
          }
        }),
        map(() => undefined),
      );
  }

  private replaceCollectionEntryWithWorkflowRoute(
    collectionId: string,
    generation: number,
  ): Observable<void> {
    return this.collectionRevisionService.getCurrentWorkflow(collectionId).pipe(
      switchMap((workflow) => {
        if (!this.isActiveCollectionOwnership(collectionId, generation)) {
          return of(undefined);
        }
        const collection = this.collection();
        if (!collection) {
          throw new Error('Collection envelope is not hydrated.');
        }
        requireCollectionWorkflowIdentity(collection, workflow, collectionId);
        const target = [...workflow.targets]
          .sort(
            (left, right) => left.order_idx_snapshot - right.order_idx_snapshot,
          )
          .find((candidate) => candidate.target_status !== 'COMPLETED');
        if (!target && isRevisionLifecycleTerminal(workflow)) {
          return this.finishTerminalIntegrationWorkflow(workflow);
        }
        if (!target) {
          throw new Error(
            'The active collection workflow has no incomplete target.',
          );
        }
        const identity = {
          collectionId: workflow.collection_id,
          revisionId: workflow.revision_id,
          targetId: target.target_id,
        };
        this.canonicalWorkflowRouteHandoff = {
          destination: identity,
          workflow,
          generation,
        };
        const navigation = this.router.navigate(
          [
            '/',
            this.activeLang,
            ...buildCollectionRevisionTargetRoute(identity),
          ],
          {
            queryParams: this.route.snapshot.queryParams,
            fragment: this.route.snapshot.fragment || undefined,
            replaceUrl: true,
          },
        );
        void navigation.then(
          (navigated) => {
            if (!navigated) {
              this.clearCanonicalWorkflowRouteHandoff(identity, generation);
            }
          },
          () => this.clearCanonicalWorkflowRouteHandoff(identity, generation),
        );
        return from(navigation).pipe(map(() => undefined));
      }),
    );
  }

  private consumeCanonicalWorkflowRouteHandoff(
    identity: CollectionRevisionTargetRouteIdentity,
    generation: number,
  ): FreshWorkflowHandoff | null {
    const handoff = this.canonicalWorkflowRouteHandoff;
    if (
      handoff &&
      handoff.generation === generation &&
      sameRevisionTargetIdentity(handoff.destination, identity)
    ) {
      this.canonicalWorkflowRouteHandoff = null;
      return handoff;
    }
    return null;
  }

  private clearCanonicalWorkflowRouteHandoff(
    identity: CollectionRevisionTargetRouteIdentity,
    generation: number,
  ): void {
    if (
      this.canonicalWorkflowRouteHandoff &&
      this.canonicalWorkflowRouteHandoff.generation === generation &&
      sameRevisionTargetIdentity(
        this.canonicalWorkflowRouteHandoff.destination,
        identity,
      )
    ) {
      this.canonicalWorkflowRouteHandoff = null;
    }
  }

  // Passcode step handlers
  onPasscodeValidated(passcode: string): void {
    this.submitPasscode(passcode, false);
  }

  private submitPasscode(passcode: string, silent: boolean): void {
    const collectionId = this.collectionId;
    const generation = this.collectionOwnershipGeneration;
    this.passcodeError.set(null);

    if (silent) {
      this.currentStep.set('loading');
    }

    this.authService
      .verifyPasscode(
        collectionId,
        passcode,
        this.collectionService.getApiUrl(),
      )
      .pipe(
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          if (!this.isActiveCollectionOwnership(collectionId, generation)) {
            return;
          }
          this.authenticatedCollection$.next({ collectionId, generation });
        },
        error: (error: VerifyPasscodeError) => {
          if (!this.isActiveCollectionOwnership(collectionId, generation)) {
            return;
          }
          const message =
            error.detail || error.message || this.getUnexpectedErrorMessage();
          this.passcodeAttempts.set(this.passcodeAttempts() + 1);
          this.passcodeError.set(message);
          this.showToast(message);

          if (error.backoff_seconds) {
            this.passcodeBackoffSeconds.set(error.backoff_seconds);
            this.startBackoffTimer();
          }

          this.currentStep.set('passcode');
        },
      });
  }

  private startBackoffTimer(): void {
    interval(1000)
      .pipe(
        takeWhile(() => this.passcodeBackoffSeconds() > 0, true),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.passcodeBackoffSeconds.set(
            Math.max(0, this.passcodeBackoffSeconds() - 1),
          );
        },
      });
  }

  // Intro step handler
  onIntroContinue(): void {
    const identity = this.getRevisionRouteIdentity();
    if (!identity || this.transitioningToTarget()) return;
    const knownBundle = this.loadedTargetBundle();
    if (
      !knownBundle ||
      !sameRevisionTargetIdentity(knownBundle.identity, identity)
    ) {
      return;
    }
    this.targetEntryRequiresIntro = false;
    this.targetEntryTransition.set({
      identity: Object.freeze({ ...identity }),
      mean: knownBundle.runtime.mean,
    });
    this.replayTargetRoute(identity);
  }

  onForcedEntityRetry(): void {
    this.onTargetRouteRetry();
  }

  onForcedEntityBack(): void {
    this.onTargetRouteErrorBack();
  }

  onResumeConnection(): void {
    if (this.resumeDialogLoading()) return;

    const exactRuntime = this.exactConnectionRuntime();
    const exactIdentity = this.getRevisionRouteIdentity();
    if (exactRuntime?.connection && exactIdentity) {
      this.showResumeDialog.set(false);
      this.showCancelConfirmDialog.set(false);
      this.authInteractionError.set(null);
      const decision = this.connectionPresenter.present(
        exactRuntime,
        'COMMAND_CONTINUATION',
      );
      this.connectionScreen.set(decision);
      this.presentExactConnectionRuntime(exactRuntime, 'COMMAND_CONTINUATION');
      if (decision.kind === 'ACTIVE_PROGRESS') {
        this.exactConnectionPollIntent$.next(exactIdentity);
      } else if (decision.kind === 'AUTH_INTERACTION') {
        this.loadExactAuthInteraction(exactRuntime);
      }
      return;
    }
  }

  private loadExactAuthInteraction(
    runtime: CollectionRevisionConnectionTargetDto,
    page = 1,
    size = 25,
  ): void {
    const connection = runtime.connection;
    const summary = connection?.auth_interaction;
    const routeIdentity = this.getRevisionRouteIdentity();
    if (!connection || !summary || !routeIdentity) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }
    const identity: CollectionRevisionAuthInteractionIdentity = Object.freeze({
      ...routeIdentity,
      connectionId: connection.connection_id,
      challengeId: summary.challenge_id,
      generation: summary.generation,
      kind: summary.kind,
    });
    const persisted = this.authInteractionPersistence.restoreDraft(identity);
    const requestedPage =
      identity.kind === 'SCOPE_SELECTION' &&
      persisted?.kind === 'SCOPE_SELECTION'
        ? persisted.page
        : page;

    this.resumeDialogLoading.set(true);
    this.connectionService
      .getAuthInteraction(identity, { page: requestedPage, size })
      .pipe(
        finalize(() => this.resumeDialogLoading.set(false)),
        catchError((error) => {
          if (isEndedAuthInteractionError(error)) {
            // Already ended on the backend: re-read the target and let it route.
            this.reloadExactAuthInteraction(identity, { ended: identity });
          } else {
            this.showToast(this.getUnexpectedErrorMessage());
            this.presentAuthInteractionResume(runtime);
          }
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        if (!detail || !this.isCurrentExactAuthIdentity(identity)) return;
        this.activateExactAuthInteraction(detail, identity, persisted);
      });
  }

  /**
   * The prompt could not be read: never leave the auth step blank or stale.
   * Fall back to the resume-or-cancel choice the route offers on hydration.
   */
  private presentAuthInteractionResume(
    runtime: CollectionRevisionConnectionTargetDto | null,
  ): void {
    // A newer runtime already took over the screen.
    if (!runtime || this.exactConnectionRuntime() !== runtime) return;
    this.clearPendingAuthInteractionState();
    this.presentExactConnectionRuntime(runtime, 'ROUTE_HYDRATION');
  }

  private isCurrentExactAuthIdentity(
    identity: CollectionRevisionAuthInteractionIdentity,
  ): boolean {
    const routeIdentity = this.getRevisionRouteIdentity();
    const runtime = this.exactConnectionRuntime();
    const connection = runtime?.connection;
    const summary = connection?.auth_interaction;
    return !!(
      routeIdentity &&
      sameRevisionTargetIdentity(routeIdentity, identity) &&
      connection?.connection_id === identity.connectionId &&
      summary?.challenge_id === identity.challengeId &&
      summary.generation === identity.generation &&
      summary.kind === identity.kind
    );
  }

  private activateExactAuthInteraction(
    detail: CollectionRevisionAuthInteractionDetailDto,
    identity: CollectionRevisionAuthInteractionIdentity,
    persisted: ReturnType<AuthInteractionPersistenceService['restoreDraft']>,
  ): void {
    const runtime = this.exactConnectionRuntime();
    const connection = runtime?.connection;
    if (!runtime || !connection || !this.isCurrentExactAuthIdentity(identity)) {
      return;
    }
    const entity = toConnectionEntityView(connection.entity);
    const authInteraction = toAuthInteractionView(
      detail,
      connection.entity.auth_capabilities,
    );
    const draftState: AuthInteractionDraftState =
      persisted?.kind === 'SCOPE_SELECTION'
        ? {
            page: persisted.page,
            size: detail.page?.size ?? 25,
            selected_ids: [...persisted.selectedScopeIds],
          }
        : { page: detail.page?.page ?? 1, size: detail.page?.size ?? 25 };

    this.selectedEntity.set(entity);
    this.setCurrentConnectionId(connection.connection_id);
    const nextState: PendingAuthInteractionState = {
      entity,
      connectionStatus: connection.status,
      authInteraction,
      exactIdentity: identity,
      draftState,
    };
    this.pendingAuthInteractionState.set(nextState);
    this.persistPendingAuthInteractionState(nextState);
    this.authInteractionError.set(null);
    this.currentStep.set('auth-interaction');
  }

  onCancelConnectionRequest(): void {
    const exactRuntime = this.exactConnectionRuntime();
    if (exactRuntime?.connection) {
      if (!exactRuntime.allowed_actions.includes('CANCEL_CONNECTION')) {
        const identity = this.getRevisionRouteIdentity();
        if (identity) this.replayTargetRoute(identity);
        return;
      }
      this.showCancelConfirmDialog.set(true);
    }
  }

  onCancelConfirmed(): void {
    const exactRuntime = this.exactConnectionRuntime();
    const exactRoute = this.getRevisionRouteIdentity();
    if (exactRuntime?.connection && exactRoute) {
      const connectionId = exactRuntime.connection.connection_id;
      this.cancelDialogLoading.set(true);
      this.resumeDialogLoading.set(true);
      this.connectionService
        .cancelConnection({ ...exactRoute, connectionId })
        .pipe(
          finalize(() => {
            this.cancelDialogLoading.set(false);
            this.resumeDialogLoading.set(false);
          }),
          takeUntil(this.stopCollectionOwnership$),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (runtime) => {
            if (
              runtime.target_status !== 'PENDING' ||
              runtime.connection !== null
            ) {
              this.showToast(this.getUnexpectedErrorMessage());
              this.replayTargetRoute(exactRoute);
              return;
            }
            this.showCancelConfirmDialog.set(false);
            this.showResumeDialog.set(false);
            this.clearPendingAuthInteractionState(
              toExactAuthInteractionIdentity(exactRuntime),
            );
            this.setCurrentConnectionId(null);
            this.applyExactConnectionCommandRuntime(runtime);
            this.replayTargetRoute(exactRoute);
          },
          error: (error) => {
            this.handleApiError(error, this.getUnexpectedErrorMessage());
            this.replayTargetRoute(exactRoute);
          },
        });
      return;
    }
  }

  onCancelDismissed(): void {
    this.showCancelConfirmDialog.set(false);
  }

  // Entity selection handlers
  onEntitySelected(entity: ConnectionEntity): void {
    const exactRuntime = this.exactConnectionRuntime();
    if (
      exactRuntime &&
      exactRuntime.target_status === 'PENDING' &&
      exactRuntime.connection === null &&
      exactRuntime.allowed_actions.includes('CONNECT')
    ) {
      this.selectedEntity.set(entity);
      this.currentConnectionId.set(null);
      this.connectionDraft.set({ kind: 'UNSELECTED' });
      this.entityOwnerWaiver.set(null);
      this.pendingConnectIntent = null;
      this.loadExactEntityOwnerWaiver(exactRuntime, entity, 'ROUTE_HYDRATION');
    }
  }

  onEntitySelectionBack(): void {
    const identity = this.getRevisionRouteIdentity();
    const workflow = this.workflow();
    if (!identity || !workflow) return;
    this.navigateRevisionDirection('BACK', identity, null, workflow);
  }

  onCompletedConnectionBack(): void {
    this.navigateCompletedConnection('BACK');
  }

  onCompletedConnectionContinue(): void {
    this.navigateCompletedConnection('NEXT');
  }

  private navigateCompletedConnection(
    direction: CollectionRevisionNavigationDirection,
  ): void {
    if (this.transitioningToTarget() || this.revisionOperationInFlight) return;
    const identity = this.getRevisionRouteIdentity();
    const workflow = this.workflow();
    if (!identity || !workflow) return;
    this.navigateRevisionDirection(direction, identity, null, workflow);
  }

  // Credentials step handlers
  onCredentialsSubmit(credentials: Record<string, string>): void {
    const exactRuntime = this.exactConnectionRuntime();
    const route = this.getRevisionRouteIdentity();
    const draft = this.connectionDraft();
    const entity = this.selectedEntity();
    if (!exactRuntime || !route || !entity) {
      this.credentialsStep?.resetLoading();
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    const existing = exactRuntime.connection;
    if (
      !existing &&
      (draft.kind !== 'READY' || draft.entity.id !== entity.id)
    ) {
      this.credentialsStep?.resetLoading();
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    const persistedId = existing?.connection_id;
    const retained = this.pendingConnectIntent;
    const intent =
      retained &&
      sameRevisionTargetIdentity(retained.route, route) &&
      retained.entityId === entity.id &&
      retained.connectionId === (persistedId ?? retained.connectionId)
        ? retained
        : {
            route: Object.freeze({ ...route }),
            connectionId: persistedId ?? createConnectionCandidateId(),
            entityId: entity.id,
            credentials: Object.freeze({ ...credentials }),
          };
    this.pendingConnectIntent = intent;

    if (
      !existing &&
      draft.kind === 'READY' &&
      draft.waiver.status !== 'NOT_REQUIRED' &&
      draft.waiver.status !== 'ACTIVE'
    ) {
      this.credentialsStep?.resetLoading();
      this.openWaiverGate(draft.waiver);
      return;
    }

    this.submitPendingConnectIntent();
  }

  private submitPendingConnectIntent(): void {
    const intent = this.pendingConnectIntent;
    const runtime = this.exactConnectionRuntime();
    const route = this.getRevisionRouteIdentity();
    if (
      !intent ||
      !runtime ||
      !route ||
      !sameRevisionTargetIdentity(intent.route, route) ||
      this.selectedEntity()?.id !== intent.entityId
    ) {
      this.pendingConnectIntent = null;
      this.credentialsStep?.resetLoading();
      return;
    }

    const existing = runtime.connection;
    const request$ = existing
      ? existing.connection_id === intent.connectionId &&
        runtime.allowed_actions.includes('UPDATE_CREDENTIALS')
        ? this.connectionService.updateCredentials(
            { ...route, connectionId: existing.connection_id },
            { credentials: { ...intent.credentials } },
          )
        : null
      : runtime.allowed_actions.includes('CONNECT')
        ? this.connectionService.connect(route, {
            connection_id: intent.connectionId,
            entity_id: intent.entityId,
            credentials: { ...intent.credentials },
          })
        : null;
    if (!request$) {
      this.pendingConnectIntent = null;
      this.credentialsStep?.resetLoading();
      this.replayTargetRoute(route);
      return;
    }

    this.toastVisible.set(false);
    this.connectionCommandBusy.set(true);
    this.credentialsStep?.beginLoading();
    request$
      .pipe(
        finalize(() => {
          this.connectionCommandBusy.set(false);
          this.credentialsStep?.resetLoading();
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (nextRuntime) => {
          this.pendingConnectIntent = null;
          this.applyExactConnectionCommandRuntime(nextRuntime);
        },
        error: (error) => {
          const parsed = parseApiError(error);
          if (parsed?.code === 'WAIVER_AUTHORIZATION_REQUIRED') {
            this.refreshWaiverGate(intent);
            return;
          }
          if (isDefinitiveConnectFailure(error)) {
            this.pendingConnectIntent = null;
          }
          this.handleCredentialsError(error);
        },
      });
  }

  private refreshWaiverGate(intent: PendingConnectIntent): void {
    this.waiverService
      .getEntityOwnerWaiver(intent.route, intent.entityId)
      .pipe(
        take(1),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (waiver) => {
          if (this.pendingConnectIntent !== intent) return;
          this.entityOwnerWaiver.set(waiver);
          const entity = this.selectedEntity();
          if (entity)
            this.connectionDraft.set({ kind: 'READY', entity, waiver });
          this.openWaiverGate(waiver);
        },
        error: (error) =>
          this.handleApiError(error, this.getUnexpectedErrorMessage()),
      });
  }

  onCredentialsBack(): void {
    const exactRuntime = this.exactConnectionRuntime();
    const exactScreen = this.connectionScreen();
    if (exactRuntime?.connection && exactScreen) {
      const decision = this.connectionPresenter.decideBack(
        exactRuntime,
        exactScreen,
        this.authenticating() || this.cancelDialogLoading(),
      );
      if (decision.kind === 'CONFIRM_CONNECTION_CANCELLATION') {
        this.showCancelConfirmDialog.set(true);
      }
      return;
    }
    if (!exactRuntime?.connection) {
      if (exactRuntime?.configured_entity === null && this.selectedEntity()) {
        this.pendingConnectIntent = null;
        this.selectedEntity.set(null);
        this.connectionDraft.set({ kind: 'UNSELECTED' });
        this.entityOwnerWaiver.set(null);
        this.currentStep.set('entity-selection');
        return;
      }
      const identity = this.getRevisionRouteIdentity();
      const workflow = this.workflow();
      if (identity && workflow) {
        this.navigateRevisionDirection('BACK', identity, null, workflow);
      }
    }
  }

  // ============================================
  // Waiver Flow Dialog Handlers
  // ============================================

  /**
   * Called when user confirms their type (rightsholder or representative) with CPF if needed
   */
  onWaiverTypeConfirmed(result: WaiverTypeSelectionResult): void {
    this.isRightsholder.set(result.isRightsholder);
    const entityType = this.entityType();

    if (result.isRightsholder) {
      // Rightsholder flow
      if (entityType === 'INDIVIDUAL') {
        this.generateIndividualWaiver();
      } else if (entityType === 'BUSINESS') {
        // Business rightsholder needs to select from representatives list
        this.showWaiverTypeDialog.set(false);
        this.loadBusinessRepresentatives();
      }
    } else {
      // Representative flow - CPF is included in result
      if (result.cpf) {
        this.representativeCpf.set(result.cpf);
        this.generateRepresentativeWaiver(result.cpf);
      }
    }
  }

  onWaiverTypeCancel(): void {
    this.showWaiverTypeDialog.set(false);
    this.showBusinessRepDialog.set(false);
    this.pendingConnectIntent = null;
    this.credentialsStep?.resetLoading();
  }

  /**
   * Called when a business representative is selected and confirmed
   */
  onBusinessRepConfirmed(selection: BusinessRepresentativeSelection): void {
    this.selectedBusinessRep.set(selection.representative);
    this.generateBusinessWaiver(selection);
  }

  onBusinessRepBack(): void {
    this.showBusinessRepDialog.set(false);
    this.showWaiverTypeDialog.set(true);
  }

  onBusinessRepCancel(): void {
    this.showBusinessRepDialog.set(false);
    this.pendingConnectIntent = null;
    this.credentialsStep?.resetLoading();
  }

  /**
   * Called when waivers are signed
   */
  /**
   * Called when user submits waiver signing from WaiverDialogComponent
   * Handles the actual API call and state management
   */
  onWaiverSubmit(): void {
    const identity = this.getRevisionRouteIdentity();
    const entity = this.selectedEntity();
    const waiver = this.entityOwnerWaiver();
    if (identity && entity && waiver?.waiver_batch_id) {
      this.waiverSigningState.set('submitting');
      this.waiverService
        .signWaiverBatch(
          identity,
          entity.id,
          waiver.waiver_batch_id,
          waiver.documents,
        )
        .pipe(
          finalize(() => {
            if (this.waiverSigningState() === 'submitting') {
              this.waiverSigningState.set('idle');
            }
          }),
          takeUntil(this.stopCollectionOwnership$),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            this.waiverSigningState.set('idle');
            this.refreshSignedWaiverAndContinue(identity, entity);
          },
          error: (error) =>
            this.handleApiError(error, this.getUnexpectedErrorMessage()),
        });
      return;
    }
  }

  onWaiverSigningCancel(): void {
    this.showWaiverSigningDialog.set(false);
    this.pendingConnectIntent = null;
    this.credentialsStep?.resetLoading();
  }

  onWaiverSigningBack(): void {
    this.showWaiverSigningDialog.set(false);
    this.showWaiverTypeDialog.set(true);
  }

  /**
   * Generate waiver for individual rightsholder
   */
  private generateIndividualWaiver(): void {
    const entity = this.selectedEntity();

    if (!entity) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    this.generateExactWaivers({
      waiver_type: 'INDIVIDUAL',
    });
  }

  private generateExactWaivers(
    request: GenerateEntityOwnerWaiverBatchRequest,
  ): void {
    const identity = this.getRevisionRouteIdentity();
    const entity = this.selectedEntity();
    if (!identity || !entity) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }
    this.waiverLoading.set(true);
    this.waiverService
      .generateWaiverBatch(identity, entity.id, request)
      .pipe(
        finalize(() => this.waiverLoading.set(false)),
        catchError((error) => {
          this.handleApiError(error, this.getUnexpectedErrorMessage());
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((waiver) => {
        if (waiver) {
          this.adoptEntityOwnerWaiver(entity, waiver);
          this.openWaiverGate(waiver);
        }
      });
  }

  /**
   * Generate waiver for representative (individual or business)
   */
  private generateRepresentativeWaiver(cpf: string): void {
    const entity = this.selectedEntity();

    if (!entity) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    this.generateExactWaivers({
      waiver_type: 'REPRESENTATIVE' as const,
      representative_document_number: cpf,
    });
  }

  /**
   * Load business entity to get representatives list
   */
  private loadBusinessRepresentatives(): void {
    const businessId = this.businessId();

    if (!businessId) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    this.businessRepsLoading.set(true);
    this.showBusinessRepDialog.set(true);

    this.waiverService
      .getBusinessEntity(this.collectionId, businessId)
      .pipe(
        finalize(() => this.businessRepsLoading.set(false)),
        catchError((error) => {
          console.error('Error loading business entity:', error);
          const parsedError = parseApiError(error);
          const errorMessage = parsedError
            ? buildErrorMessage(parsedError)
            : this.getUnexpectedErrorMessage();
          this.showToast(errorMessage);
          this.showBusinessRepDialog.set(false);
          this.showWaiverTypeDialog.set(true);
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.businessEntity.set(response);
            this.businessRepresentatives.set(response.representatives);
          }
        },
      });
  }

  /**
   * Generate waiver for business rightsholder (legal representative)
   */
  private generateBusinessWaiver(
    selection: BusinessRepresentativeSelection,
  ): void {
    const entity = this.selectedEntity();
    const businessId = this.businessId();

    if (!entity || !businessId) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    this.generateExactWaivers({
      waiver_type: 'BUSINESS',
      representative_document_number: selection.representativeDocumentNumber,
      representative_role: selection.representative.role,
    });
  }

  private openWaiverGate(waiver: EntityOwnerWaiverDto): void {
    this.showWaiverTypeDialog.set(false);
    this.showBusinessRepDialog.set(false);
    this.showWaiverSigningDialog.set(false);
    this.waiverDocuments.set(
      waiver.documents.map((document) =>
        toWaiverDocumentView(document, waiver.validity_days),
      ),
    );
    if (
      waiver.status === 'AWAITING_SIGNATURE' &&
      waiver.waiver_batch_id &&
      waiver.documents.length > 0
    ) {
      this.showWaiverSigningDialog.set(true);
      return;
    }
    if (waiver.status === 'MISSING' || waiver.status === 'EXPIRED') {
      this.showWaiverTypeDialog.set(true);
      return;
    }
    if (waiver.status === 'ACTIVE' || waiver.status === 'NOT_REQUIRED') {
      this.submitPendingConnectIntent();
    }
  }

  private adoptEntityOwnerWaiver(
    entity: ConnectionEntity,
    waiver: EntityOwnerWaiverDto,
  ): void {
    this.entityOwnerWaiver.set(waiver);
    this.connectionDraft.set({ kind: 'READY', entity, waiver });
    this.waiverDocuments.set(
      waiver.documents.map((document) =>
        toWaiverDocumentView(document, waiver.validity_days),
      ),
    );
  }

  private refreshSignedWaiverAndContinue(
    identity: CollectionRevisionTargetRouteIdentity,
    entity: ConnectionEntity,
  ): void {
    this.waiverService
      .getEntityOwnerWaiver(identity, entity.id)
      .pipe(
        take(1),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (waiver) => {
          this.adoptEntityOwnerWaiver(entity, waiver);
          if (waiver.status !== 'ACTIVE') {
            this.openWaiverGate(waiver);
            return;
          }
          this.showWaiverSigningDialog.set(false);
          this.showWaiverTypeDialog.set(false);
          if (this.pendingConnectIntent) {
            this.submitPendingConnectIntent();
          } else {
            this.credentialsStep?.resetLoading();
          }
        },
        error: (error) =>
          this.handleApiError(error, this.getUnexpectedErrorMessage()),
      });
  }

  /**
   * Clear waiver flow state
   */
  private clearWaiverState(): void {
    this.entityOwnerWaiver.set(null);
    this.connectionDraft.set({ kind: 'UNSELECTED' });
    this.pendingConnectIntent = null;
    this.requiresWaiver.set(false);
    this.waiverDocuments.set([]);
    this.waiverSigningState.set('idle');
    this.waiverLoading.set(false);
    this.isRightsholder.set(false);
    this.representativeCpf.set(null);
    this.businessEntity.set(null);
    this.businessRepresentatives.set([]);
    this.selectedBusinessRep.set(null);
    this.showWaiverTypeDialog.set(false);
    this.showBusinessRepDialog.set(false);
    this.showWaiverSigningDialog.set(false);
  }

  // ============================================
  // End Waiver Flow Handlers
  // ============================================

  onAuthInteractionSubmit(request: SubmitAuthInteractionRequest): void {
    const pendingState = this.pendingAuthInteractionState();
    if (!pendingState) {
      return;
    }
    this.submitExactAuthInteraction(pendingState, request);
  }

  private submitExactAuthInteraction(
    pendingState: PendingAuthInteractionState,
    request: SubmitAuthInteractionRequest,
  ): void {
    const identity = pendingState.exactIdentity;
    if (request.kind !== identity.kind) {
      this.authInteractionError.set(this.getUnexpectedErrorMessage());
      return;
    }
    this.authInteractionLoading.set(true);
    this.authInteractionError.set(null);
    this.connectionService
      .submitAuthInteraction(identity, { payload: request.payload })
      .pipe(
        finalize(() => this.authInteractionLoading.set(false)),
        catchError((error) => {
          this.failAuthInteractionRequest(identity, error, true);
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((runtime) => {
        if (!runtime || !this.isCurrentExactAuthIdentity(identity)) return;
        this.applyExactConnectionCommandRuntime(runtime);
      });
  }

  onAuthInteractionBack(): void {
    this.onCancelConnectionRequest();
  }

  /** The verification already ended: nothing to cancel, just reconnect. */
  onAuthInteractionEndedBack(): void {
    this.connectionScreen.set({ kind: 'CREDENTIALS', mode: 'REAUTH' });
    this.credentialsMode.set('reauth');
    this.currentStep.set('credentials');
  }

  /**
   * TENTAR DE NOVO after setting up two-factor on the provider's site. The
   * credentials step opens without a failure banner: the setup screen already
   * explained it, and the reauth banner would claim an expired session.
   */
  onProviderSetupRetry(): void {
    this.connectionScreen.set({ kind: 'CREDENTIALS', mode: 'INITIAL' });
    this.credentialsMode.set('initial');
    this.currentStep.set('credentials');
  }

  /** Back from the setup screen behaves like Back from the credentials step. */
  onProviderSetupBack(): void {
    this.onCredentialsBack();
  }

  onAuthInteractionResend(): void {
    const pendingState = this.pendingAuthInteractionState();
    if (!pendingState?.authInteraction.resend_supported) return;
    if (this.pushCheckInFlight()) {
      // The backend claims the prompt during a push check, so a resend now
      // would be refused: show it as started and send it once the check
      // settles.
      this.authInteractionResending.set(true);
      this.resendAfterPushCheck = true;
      return;
    }
    this.resendExactAuthInteraction(pendingState);
  }

  private resendExactAuthInteraction(
    pendingState: PendingAuthInteractionState,
  ): void {
    const identity = pendingState.exactIdentity;
    if (!pendingState.authInteraction.resend_supported) return;
    this.authInteractionResending.set(true);
    this.authInteractionError.set(null);
    this.connectionService
      .resendAuthInteraction(identity)
      .pipe(
        finalize(() => this.authInteractionResending.set(false)),
        catchError((error) => {
          if (
            parseApiError(error)?.code === 'AUTH_INTERACTION_RESEND_THROTTLED'
          ) {
            // No new code was sent; the reloaded prompt shows the wait.
            this.reloadExactAuthInteraction(identity);
            return of(null);
          }
          this.failAuthInteractionRequest(identity, error);
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((runtime) => {
        if (!runtime || !this.isCurrentExactAuthIdentity(identity)) return;
        this.applyExactConnectionCommandRuntime(runtime);
        this.showToast(
          this.translocoService.translate(
            identity.kind === 'PUSH_APPROVAL'
              ? 'auth_interaction_push_resend_success'
              : 'auth_interaction_resend_success',
            {},
            'mzic-collection-flow',
          ),
          'success',
        );
      });
  }

  /**
   * A failed verification request that is not a typed outcome: the flow's
   * standard toast, never backend text (provider 2FA product rule), then the
   * target is re-read so the screen follows the backend. An ended interaction
   * (410) is typed: it re-routes to the ended screen without a toast.
   * `holdsCode`: a lost code submit keeps the entry held through the re-read
   * (a newer prompt is its answer; a rejected code is never offered again).
   */
  private failAuthInteractionRequest(
    identity: CollectionRevisionAuthInteractionIdentity,
    error: unknown,
    holdsCode = false,
  ): void {
    if (isEndedAuthInteractionError(error)) {
      this.reloadExactAuthInteraction(identity, { ended: identity });
      return;
    }
    this.showToast(this.getUnexpectedErrorMessage());
    this.reloadExactAuthInteraction(identity, holdsCode ? { held: identity } : {});
  }

  /**
   * Re-reads the target first: the prompt generation may have moved on.
   * - `ended`: a prompt the backend just called ended. If the target still
   *   lists it, the route offers resume-or-cancel instead of reading it again
   *   (no request loop on inconsistent backend state).
   * - `held`: a prompt whose code entry waits for this answer. It is released
   *   only if the target still lists that same prompt or the read fails.
   */
  private reloadExactAuthInteraction(
    identity: CollectionRevisionTargetRouteIdentity,
    prompt: {
      readonly ended?: CollectionRevisionAuthInteractionIdentity;
      readonly held?: CollectionRevisionAuthInteractionIdentity;
    } = {},
  ): void {
    this.connectionService
      .getConnectionTarget(identity)
      .pipe(
        catchError(() => {
          this.showToast(this.getUnexpectedErrorMessage());
          if (prompt.held) this.releaseHeldCode(prompt.held);
          // Never leave the auth step blank.
          if (!this.pendingAuthInteractionState()) {
            this.presentAuthInteractionResume(this.exactConnectionRuntime());
          }
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((runtime) => {
        if (!runtime) return;
        const summary = runtime.connection?.auth_interaction;
        const lists = (
          candidate: CollectionRevisionAuthInteractionIdentity | undefined,
        ) =>
          !!candidate &&
          summary?.challenge_id === candidate.challengeId &&
          summary.generation === candidate.generation;
        if (prompt.ended && lists(prompt.ended)) {
          if (!this.isCurrentExactAuthIdentity(prompt.ended)) return;
          this.showToast(this.getUnexpectedErrorMessage());
          this.clearPendingAuthInteractionState();
          this.applyExactConnectionCommandRuntime(runtime, 'ROUTE_HYDRATION');
          return;
        }
        // The lost submit never reached the provider: the code may go again.
        if (prompt.held && lists(prompt.held)) {
          this.releaseHeldCode(prompt.held);
        }
        this.applyExactConnectionCommandRuntime(runtime);
      });
  }

  /** Lets a code entry held for a lost submit be edited and sent again. */
  private releaseHeldCode(
    prompt: CollectionRevisionAuthInteractionIdentity,
  ): void {
    if (this.isCurrentExactAuthIdentity(prompt)) {
      this.authInteractionError.set(this.getUnexpectedErrorMessage());
    }
  }

  onAuthInteractionPageRequested(params: { page: number; size: number }): void {
    const pendingState = this.pendingAuthInteractionState();
    if (
      !pendingState ||
      this.authInteractionLoading() ||
      this.authInteractionPageLoading()
    ) {
      return;
    }
    this.loadExactAuthInteractionPage(pendingState, params);
  }

  private loadExactAuthInteractionPage(
    pendingState: PendingAuthInteractionState,
    params: { page: number; size: number },
  ): void {
    const identity = pendingState.exactIdentity;
    this.authInteractionPageLoading.set(true);
    this.authInteractionError.set(null);
    this.connectionService
      .getAuthInteraction(identity, params)
      .pipe(
        finalize(() => this.authInteractionPageLoading.set(false)),
        catchError((error) => {
          this.failAuthInteractionRequest(identity, error);
          return of(null);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        if (!detail || !this.isCurrentExactAuthIdentity(identity)) return;
        const nextView = toAuthInteractionView(
          detail,
          pendingState.entity?.auth_capabilities ?? [],
        );
        const merged = this.mergeAuthInteractionPage(
          pendingState.authInteraction,
          nextView,
        );
        if (!merged) return;
        const nextState: PendingAuthInteractionState = {
          ...pendingState,
          authInteraction: merged,
          draftState: {
            ...pendingState.draftState,
            page: params.page,
            size: params.size,
          },
        };
        this.pendingAuthInteractionState.set(nextState);
        this.persistPendingAuthInteractionState(nextState);
      });
  }

  private applyExactConnectionCommandRuntime(
    runtime: CollectionRevisionConnectionTargetDto,
    origin: ConnectionPresentationOrigin = 'COMMAND_CONTINUATION',
  ): void {
    const identity = this.getRevisionRouteIdentity();
    if (
      !identity ||
      runtime.collection_id !== identity.collectionId ||
      runtime.revision_id !== identity.revisionId ||
      runtime.target_id !== identity.targetId
    ) {
      return;
    }
    const bundle = this.loadedTargetBundle();
    if (bundle?.runtime.mean === 'CONNECTION') {
      this.loadedTargetBundle.set({
        ...bundle,
        runtime: { mean: 'CONNECTION', connectionTarget: runtime },
      });
    }
    this.presentExactConnectionRuntime(
      runtime,
      origin,
      (decision) => {
        if (decision.kind === 'AUTH_INTERACTION') {
          this.loadExactAuthInteraction(runtime);
        } else if (decision.kind === 'ACTIVE_PROGRESS') {
          this.clearPendingAuthInteractionState();
          this.exactConnectionPollIntent$.next(identity);
        }
      },
    );
  }

  onAuthInteractionDraftChanged(draftState: AuthInteractionDraftState): void {
    const pendingState = this.pendingAuthInteractionState();
    if (!pendingState) {
      return;
    }

    const nextState: PendingAuthInteractionState = {
      ...pendingState,
      draftState,
    };

    this.pendingAuthInteractionState.set(nextState);
    this.persistPendingAuthInteractionState(nextState);
  }

  private mergeAuthInteractionPage(
    current: AuthInteraction,
    next?: AuthInteraction | null,
  ): AuthInteraction | null {
    if (!next?.page || !current.page || next.kind !== current.kind) {
      return next ?? null;
    }

    if (next.page.page <= 1) {
      return next;
    }

    const itemsById = new Map(
      current.page.items.map((item) => [item.id, item]),
    );

    for (const item of next.page.items) {
      itemsById.set(item.id, item);
    }

    return {
      ...next,
      page: {
        ...next.page,
        items: [...itemsById.values()],
      },
    };
  }

  onRevisionFileNavigationRequested(
    direction: CollectionRevisionNavigationDirection,
  ): void {
    if (this.revisionOperationInFlight || this.fileEditorState().busy) return;
    const decision = this.revisionFileEditor.requestNavigation(direction);
    if (decision.kind === 'NAVIGATE') {
      this.completeRevisionFileNavigation(direction);
    }
  }

  onRevisionFilePrimaryActionRequested(): void {
    if (this.revisionOperationInFlight || this.fileEditorState().busy) return;
    const decision = this.revisionFileEditor.requestPrimaryAction();
    if (decision.kind === 'SUBMIT_INITIAL') {
      this.beginRevisionFileOperation();
      this.revisionFileEditor
        .submitInitialFileSet(this.requireWorkflow())
        .pipe(
          takeUntil(this.stopCollectionOwnership$),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (result) => this.applyRevisionNavigationResult(result),
          error: (error) => {
            this.revisionOperationInFlight = false;
            if (!this.fileEditorState().conflict) {
              this.handleRevisionFileOperationError(error);
            }
          },
        });
      return;
    }
    if (decision.kind === 'NAVIGATE') {
      this.completeRevisionFileNavigation('NEXT');
    }
  }

  private completeRevisionFileNavigation(
    direction: CollectionRevisionNavigationDirection,
  ): void {
    this.beginRevisionFileOperation();
    this.revisionFileEditor
      .completeCleanNavigation(direction, this.requireWorkflow())
      .pipe(
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => this.applyRevisionNavigationResult(result),
        error: (error) => {
          this.revisionOperationInFlight = false;
          this.handleApiError(error, this.getUnexpectedErrorMessage());
        },
      });
  }

  onFilePendingDecisionStay(): void {
    const direction = this.fileEditorState().pendingDecision?.direction;
    if (!direction) return;
    this.revisionFileEditor.cancelPendingDecision();
    if (direction === 'EXTERNAL') {
      this.pendingExternalNavigationUrl = null;
      return;
    }
    const pendingBrowserRoute = this.pendingBrowserRouteIdentity;
    this.pendingBrowserRouteIdentity = null;
    if (pendingBrowserRoute && this.activeRevisionIdentity) {
      void this.navigateToRevisionTarget(this.activeRevisionIdentity, true);
    }
    this.fileUploadStep?.focusNavigationControl(direction);
  }

  onFilePendingDecisionDiscard(): void {
    const decision = this.revisionFileEditor.discardPendingDecision();
    if (decision.kind === 'NAVIGATE') {
      if (decision.direction === 'EXTERNAL') {
        const destination = this.pendingExternalNavigationUrl;
        this.pendingExternalNavigationUrl = null;
        if (!destination) {
          throw new Error('An external navigation destination is required.');
        }
        this.resumeExternalNavigation(destination);
        return;
      }
      const identity = this.requireActiveRevisionIdentity();
      const pendingBrowserRoute = this.pendingBrowserRouteIdentity;
      this.pendingBrowserRouteIdentity = null;
      if (pendingBrowserRoute) {
        this.replayTargetRoute(pendingBrowserRoute);
        return;
      }
      this.navigateRevisionDirection(decision.direction, identity);
    }
  }

  onFileUpdateDecisionConfirm(): void {
    if (this.fileEditorState().conflict || this.revisionOperationInFlight)
      return;
    this.beginRevisionFileOperation();
    const workflow = this.requireWorkflow();
    this.revisionFileEditor
      .confirmNavigationDecision(workflow)
      .pipe(
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => this.applyRevisionNavigationResult(result),
        error: (error) => {
          this.revisionOperationInFlight = false;
          if (!this.fileEditorState().conflict) {
            this.handleRevisionFileOperationError(error);
          }
        },
      });
  }

  onFileConflictRebase(): void {
    if (
      this.revisionOperationInFlight ||
      this.fileEditorState().conflict?.editable !== true
    )
      return;
    this.beginRevisionFileOperation();
    this.revisionFileEditor
      .rebaseConflictAndRetry(this.requireWorkflow())
      .pipe(
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => this.applyRevisionNavigationResult(result),
        error: (error) => {
          this.revisionOperationInFlight = false;
          if (!this.fileEditorState().conflict) {
            this.handleRevisionFileOperationError(error);
          }
        },
      });
  }

  onFileConflictReload(): void {
    const current = this.fileEditorState().conflict?.current.target_file_set;
    if (
      this.revisionOperationInFlight ||
      this.fileEditorState().busy ||
      !current ||
      !this.revisionFileEditor.discardConflictAndReload()
    )
      return;
    this.activeRevisionIdentity = {
      collectionId: current.collection_id,
      revisionId: current.revision_id,
      targetId: current.target_id,
    };
    this.currentTargetId.set(current.target_id);
    void this.router.navigate(
      [
        '/',
        this.activeLang,
        ...buildCollectionRevisionTargetRoute(this.activeRevisionIdentity),
      ],
      {
        queryParams: this.route.snapshot.queryParams,
        fragment: this.route.snapshot.fragment || undefined,
        replaceUrl: true,
      },
    );
  }

  private requireWorkflow(): CollectionWorkflowDto {
    const workflow = this.workflow();
    if (!workflow) throw new Error('Collection workflow is not hydrated.');
    return workflow;
  }

  private beginRevisionFileOperation(): void {
    this.clearErrorToast();
    this.showFileValidationIssuesDialog.set(false);
    this.revisionOperationInFlight = true;
  }

  private handleRevisionFileOperationError(error: unknown): void {
    if (error instanceof CollectionRevisionFileValidationError) {
      this.showFileValidationIssuesDialog.set(true);
      return;
    }
    if (this.fileEditorState().failure?.kind === 'UPLOAD') {
      return;
    }
    this.handleApiError(error, this.getUnexpectedErrorMessage());
  }

  onFileValidationReviewReports(): void {
    if (
      this.presentedFileValidationIssues().some(
        (issue) => issue.kind === 'PERMANENT',
      )
    ) {
      return;
    }
    this.showFileValidationIssuesDialog.set(false);
    this.openFileRequiredInputsReview();
  }

  onFileValidationBack(): void {
    this.showFileValidationIssuesDialog.set(false);
  }

  onFileValidationRemoveFiles(fileIds: readonly string[]): void {
    if (this.revisionOperationInFlight || this.fileEditorState().busy) return;
    this.revisionOperationInFlight = true;
    this.revisionFileEditor
      .discardValidationFiles(fileIds)
      .pipe(
        finalize(() => (this.revisionOperationInFlight = false)),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.showFileValidationIssuesDialog.set(
            this.fileValidationIssues().length > 0,
          );
        },
        error: (error) => {
          this.handleApiError(error, this.getUnexpectedErrorMessage());
        },
      });
  }

  onRevisionFileReviewRequiredInputs(): void {
    if (this.fileValidationIssues().some(({ kind }) => kind === 'PERMANENT')) {
      this.showFileRequiredInputsDialog.set(false);
      this.showFileValidationIssuesDialog.set(true);
      return;
    }
    this.openFileRequiredInputsReview();
  }

  onFileRequiredInputsReviewClose(): void {
    if (this.fileRequiredInputsReviewBusy()) return;
    this.showFileRequiredInputsDialog.set(false);
    this.fileRequiredInputsReviewError.set('');
  }

  /** Revision scope handed to the statement-origin picker inside the dialog. */
  get requiredInputsCollectionId(): string {
    return this.getRevisionRouteIdentity()?.collectionId ?? '';
  }

  get requiredInputsRevisionId(): string {
    return this.getRevisionRouteIdentity()?.revisionId ?? '';
  }

  onFileRequiredInputsSubmit(submission: FileRequiredInputsSubmission): void {
    if (
      this.fileRequiredInputsReviewBusy() ||
      this.fileEditorState().busy ||
      submission.fileId !== this.currentFileRequiredInputsReview()?.fileId
    ) {
      return;
    }
    let accepted = true;
    for (const [key, value] of Object.entries(submission.resolvedInputs)) {
      accepted =
        this.revisionFileEditor.resolveRequiredInput(
          submission.fileId,
          key,
          value,
        ) && accepted;
    }
    if (!accepted) {
      this.fileRequiredInputsReviewError.set(
        this.collectionFlowTranslation(
          'file_required_inputs_invalid',
          'Não foi possível aplicar a informação deste relatório.',
          'The report information could not be applied.',
        ),
      );
      return;
    }

    this.fileRequiredInputsReviewBusy.set(true);
    this.fileRequiredInputsReviewError.set('');
    this.revisionFileEditor
      .revalidateRequiredInput(submission.fileId)
      .pipe(
        finalize(() => this.fileRequiredInputsReviewBusy.set(false)),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (validation) => {
          if (
            !validation.usable &&
            isStatementOriginValidationErrorCode(validation.error_code)
          ) {
            // Resolvable requirement, not a permanent rejection: the state
            // machine keeps the origin descriptor, so the dialog stays open on
            // this report with the localized reason (D-09/D-10).
            this.fileRequiredInputsReviewError.set(
              this.statementOriginRejectionMessage(validation.error_code),
            );
            return;
          }
          if (validation.usable && validation.required_inputs.length === 0) {
            this.advanceFileRequiredInputsReview();
            return;
          }
          if (validation.required_inputs.length === 0) {
            this.showFileRequiredInputsDialog.set(false);
            this.showFileValidationIssuesDialog.set(true);
            return;
          }
          this.fileRequiredInputsReviewError.set(
            this.collectionFlowTranslation(
              'file_required_inputs_still_required',
              'A informação ainda não pôde ser validada. Revise a resposta e tente novamente.',
              'The information could not be validated yet. Review the answer and try again.',
            ),
          );
        },
        error: () => {
          this.fileRequiredInputsReviewError.set(
            this.collectionFlowTranslation(
              'file_required_inputs_retry_error',
              'Não foi possível validar este relatório. Tente novamente.',
              'This report could not be validated. Please try again.',
            ),
          );
        },
      });
  }

  onRevisionFileRetryUpload(clientId: string): void {
    if (this.revisionOperationInFlight || this.fileEditorState().busy) return;
    this.revisionOperationInFlight = true;
    this.revisionFileEditor
      .retryFailedUpload(clientId)
      .pipe(
        finalize(() => (this.revisionOperationInFlight = false)),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (allUploadsStaged) => {
          if (allUploadsStaged) {
            queueMicrotask(() => this.onRevisionFilePrimaryActionRequested());
          }
        },
        error: () => undefined,
      });
  }

  private openFileRequiredInputsReview(): void {
    const unresolved = this.unresolvedRequiredInputFileIds();
    if (unresolved.length === 0) {
      this.showFileRequiredInputsDialog.set(false);
      return;
    }
    const activeFileIds = new Set(
      this.fileEditorState().rows.flatMap((row) => {
        const fileId = this.revisionFileEditor.fileIdForRow(row);
        return fileId ? [fileId] : [];
      }),
    );
    const currentQueue = this.fileRequiredInputsReviewQueue();
    const retained = currentQueue.filter((fileId) => activeFileIds.has(fileId));
    const queue = [
      ...retained,
      ...unresolved.filter((fileId) => !retained.includes(fileId)),
    ];
    const currentIndex = queue.findIndex((fileId) =>
      unresolved.includes(fileId),
    );
    this.fileRequiredInputsReviewQueue.set(queue);
    this.fileRequiredInputsReviewIndex.set(
      currentIndex >= 0 ? currentIndex : 0,
    );
    this.fileRequiredInputsReviewError.set('');
    this.showFileRequiredInputsDialog.set(true);
  }

  private advanceFileRequiredInputsReview(): void {
    const queue = this.fileRequiredInputsReviewQueue();
    const unresolved = new Set(this.unresolvedRequiredInputFileIds());
    const current = this.fileRequiredInputsReviewIndex();
    let next = queue.findIndex(
      (fileId, index) => index > current && unresolved.has(fileId),
    );
    if (next < 0) {
      next = queue.findIndex((fileId) => unresolved.has(fileId));
    }
    if (next < 0) {
      this.showFileRequiredInputsDialog.set(false);
      this.fileRequiredInputsReviewQueue.set([]);
      this.fileRequiredInputsReviewIndex.set(0);
      this.fileRequiredInputsReviewError.set('');
      return;
    }
    this.fileRequiredInputsReviewIndex.set(next);
    this.fileRequiredInputsReviewError.set('');
  }

  private unresolvedRequiredInputFileIds(): readonly string[] {
    const state = this.fileEditorState();
    return state.rows.flatMap((row) => {
      const fileId = this.revisionFileEditor.fileIdForRow(row);
      return fileId && (state.requiredInputsByFile[fileId]?.length ?? 0) > 0
        ? [fileId]
        : [];
    });
  }

  private statementOriginRejectionMessage(
    errorCode: StatementOriginValidationErrorCode,
  ): string {
    switch (errorCode) {
      case 'STATEMENT_ORIGIN_NOT_VISIBLE':
        return this.collectionFlowTranslation(
          'file_required_inputs_statement_origin_not_visible',
          'Esta fonte pagadora não está disponível para esta coleção. Selecione outra.',
          'This payor is not available for this collection. Select another one.',
        );
      case 'STATEMENT_ORIGIN_CONSTRAINT_VIOLATION':
        return this.collectionFlowTranslation(
          'file_required_inputs_statement_origin_constraint_violation',
          'Esta fonte pagadora não é aceita para este tipo de relatório. Selecione outra.',
          'This payor is not accepted for this report type. Select another one.',
        );
      case 'STATEMENT_ORIGIN_INACTIVE':
        return this.collectionFlowTranslation(
          'file_required_inputs_statement_origin_inactive',
          'Esta fonte pagadora está inativa. Selecione outra.',
          'This payor is inactive. Select another one.',
        );
      case 'STATEMENT_ORIGIN_NOT_FOUND':
        return this.collectionFlowTranslation(
          'file_required_inputs_statement_origin_not_found',
          'Fonte pagadora não encontrada. Selecione outra.',
          'Payor not found. Select another one.',
        );
      default:
        return this.collectionFlowTranslation(
          'file_required_inputs_statement_origin_rejected',
          'Não foi possível confirmar a fonte pagadora. Selecione outra e tente novamente.',
          'The payor could not be confirmed. Select another one and try again.',
        );
    }
  }

  private fileValidationIssueReason(
    validation: CollectionRevisionFileValidationSnapshot,
  ): string {
    const reasonCode = this.validationDecisionText(
      validation.decision,
      'reason_code',
    );
    if (reasonCode === 'MIXED_REVENUE_CLASSIFICATION') {
      return this.collectionFlowTranslation(
        'file_validation_reason_mixed_revenue_classification',
        'Este relatório contém mais de uma classificação de receita e não pode ser utilizado como um único relatório.',
        'This report contains more than one revenue classification and cannot be used as a single report.',
      );
    }
    return (
      this.validationDecisionText(validation.decision, 'reason') ||
      validation.message ||
      validation.error_code ||
      this.collectionFlowTranslation(
        'file_validation_unusable_default',
        'O relatório não pôde ser validado.',
        'The report could not be validated.',
      )
    );
  }

  private validationDecisionText(
    decision: Readonly<Record<string, unknown>> | null,
    key: string,
  ): string | null {
    const value = decision?.[key];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private collectionFlowTranslation(
    key: string,
    portugueseFallback: string,
    englishFallback: string,
  ): string {
    const translated = this.translocoService.translate(
      key,
      {},
      'mzic-collection-flow',
    );
    if (translated && translated !== key && !translated.includes(key)) {
      return translated;
    }
    return this.translocoService.getActiveLang() === 'pt-br'
      ? portugueseFallback
      : englishFallback;
  }

  private applyRevisionNavigationResult(
    result: CollectionRevisionFileEditorCommitResult,
  ): void {
    if (result.direction === 'EXTERNAL') {
      const destination = this.pendingExternalNavigationUrl;
      this.pendingExternalNavigationUrl = null;
      if (!destination) {
        this.releaseRevisionNavigationBarrier();
        throw new Error('An external navigation destination is required.');
      }
      this.resumeExternalNavigation(destination);
      return;
    }
    const pendingBrowserRoute = this.pendingBrowserRouteIdentity;
    this.pendingBrowserRouteIdentity = null;
    if (pendingBrowserRoute) {
      const effectiveDestination = {
        ...pendingBrowserRoute,
        collectionId: result.identity.collectionId,
        revisionId: result.identity.revisionId,
      };
      this.setCanonicalWorkflowRouteHandoff(
        effectiveDestination,
        result.workflow,
      );
      if (
        sameRevisionTargetIdentity(pendingBrowserRoute, effectiveDestination)
      ) {
        this.replayTargetRoute(effectiveDestination, true);
      } else {
        this.settleRevisionRouteNavigation(
          this.navigateToRevisionTarget(effectiveDestination, true),
        );
      }
      return;
    }
    this.navigateRevisionDirection(
      result.direction,
      result.identity,
      result.authorization,
      result.workflow,
    );
  }

  private requireActiveRevisionIdentity(): CollectionRevisionTargetRouteIdentity {
    const identity = this.getRevisionRouteIdentity();
    if (!identity) {
      throw new Error('A revision target route is required for file editing.');
    }
    return identity;
  }

  private navigateRevisionDirection(
    direction: CollectionRevisionNavigationDirection,
    current: CollectionRevisionTargetRouteIdentity,
    authorization: CollectionRevisionFileEditorCommitResult['authorization'] = null,
    workflowOverride?: CollectionWorkflowDto,
  ): void {
    const workflow = workflowOverride ?? this.requireWorkflow();
    const intent =
      direction === 'BACK'
        ? getPreviousRevisionTargetIntent(workflow.targets, current.targetId)
        : getNextRevisionTargetIntent(workflow.targets, current.targetId);
    if (direction === 'BACK' && !intent) {
      this.targetEntryRequiresIntro = true;
      this.prepareIntroBeforeTarget();
      this.releaseRevisionNavigationBarrier();
      return;
    }
    if (!intent || intent.kind === 'complete-flow') {
      if (isRevisionLifecycleTerminal(workflow)) {
        this.showRevisionComplete(workflow);
        if (authorization?.shouldRedirect) {
          this.startRedirectCountdown(authorization.url);
        }
        this.releaseRevisionNavigationBarrier();
      } else {
        this.refreshRevisionWorkflowAndRoute(true);
      }
      return;
    }
    const destination = {
      collectionId: workflow.collection_id,
      revisionId: workflow.revision_id,
      targetId: intent.targetId,
    };
    if (workflowOverride) {
      this.setCanonicalWorkflowRouteHandoff(destination, workflow);
    }
    this.settleRevisionRouteNavigation(
      this.navigateToRevisionTarget(destination),
    );
  }

  private setCanonicalWorkflowRouteHandoff(
    destination: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
  ): void {
    if (
      workflow.collection_id !== destination.collectionId ||
      workflow.revision_id !== destination.revisionId ||
      !workflow.targets.some(
        (target) => target.target_id === destination.targetId,
      )
    ) {
      throw new Error(
        'A workflow handoff must identify its exact navigation destination.',
      );
    }
    this.canonicalWorkflowRouteHandoff = {
      destination: Object.freeze({ ...destination }),
      workflow,
      generation: this.collectionOwnershipGeneration,
    };
  }

  private navigateToRevisionTarget(
    identity: CollectionRevisionTargetRouteIdentity,
    replaceUrl = false,
  ): Promise<boolean> {
    const generation = this.collectionOwnershipGeneration;
    const preparedTargetEntryTransition =
      this.prepareKnownTargetEntryTransition(identity);
    const commands = [
      '/',
      this.activeLang,
      ...buildCollectionRevisionTargetRoute(identity),
    ];
    const routeState = {
      queryParams: this.route.snapshot.queryParams,
      fragment: this.route.snapshot.fragment || undefined,
    };
    const destination = this.router.serializeUrl(
      this.router.createUrlTree(commands, routeState),
    );
    this.authorizedNavigationUrl = destination;

    let navigation: Promise<boolean>;
    try {
      navigation = this.router.navigate(commands, {
        ...routeState,
        ...(replaceUrl ? { replaceUrl: true } : {}),
      });
    } catch (error) {
      if (preparedTargetEntryTransition) {
        this.clearTargetEntryTransition(identity);
      }
      this.authorizedNavigationUrl = null;
      throw error;
    }

    return navigation
      .then(
        (navigated) => {
          if (!navigated && preparedTargetEntryTransition) {
            this.clearTargetEntryTransition(identity);
          }
          return navigated;
        },
        (error: unknown) => {
          if (preparedTargetEntryTransition) {
            this.clearTargetEntryTransition(identity);
          }
          throw error;
        },
      )
      .finally(() => {
        if (
          this.isActiveCollectionOwnership(identity.collectionId, generation) &&
          this.authorizedNavigationUrl === destination
        ) {
          this.authorizedNavigationUrl = null;
        }
      });
  }

  private prepareKnownTargetEntryTransition(
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto | null = this.workflow(),
  ): boolean {
    const active = this.activeRevisionIdentity;
    if (
      !active ||
      sameRevisionTargetIdentity(active, identity) ||
      !workflow ||
      workflow.collection_id !== identity.collectionId ||
      workflow.revision_id !== identity.revisionId
    ) {
      return false;
    }

    const target = workflow.targets.find(
      (candidate) => candidate.target_id === identity.targetId,
    );
    const mean = target?.mean_type_snapshot;
    if (mean !== 'UPLOAD' && mean !== 'CONNECTION') {
      return false;
    }

    this.targetEntryTransition.set({
      identity: Object.freeze({ ...identity }),
      mean,
    });
    return true;
  }

  private clearTargetEntryTransition(
    identity: CollectionRevisionTargetRouteIdentity,
  ): void {
    const transition = this.targetEntryTransition();
    if (
      transition &&
      sameRevisionTargetIdentity(transition.identity, identity)
    ) {
      this.targetEntryTransition.set(null);
    }
  }

  private replayTargetRoute(
    identity: CollectionRevisionTargetRouteIdentity,
    releaseNavigationBarrier = false,
  ): void {
    this.retryTargetRoute$.next({
      identity: Object.freeze({ ...identity }),
      releaseNavigationBarrier,
    });
  }

  private settleRevisionRouteNavigation(
    navigation: Promise<boolean>,
    collectionId = this.collectionId,
    generation = this.collectionOwnershipGeneration,
  ): void {
    void navigation.then(
      () => {
        if (this.isActiveCollectionOwnership(collectionId, generation)) {
          this.releaseRevisionNavigationBarrier();
        }
      },
      (error: unknown) => {
        if (!this.isActiveCollectionOwnership(collectionId, generation)) {
          return;
        }
        this.releaseRevisionNavigationBarrier();
        this.handleApiError(error, this.getUnexpectedErrorMessage());
      },
    );
  }

  private releaseRevisionNavigationBarrier(): void {
    if (this.fileEditorState().busy) {
      this.revisionFileEditor.completeNavigation();
    }
    this.revisionOperationInFlight = false;
  }

  canDeactivateCollectionRoute(nextUrl: string): boolean {
    if (this.authorizedNavigationUrl === nextUrl) {
      this.authorizedNavigationUrl = null;
      return true;
    }
    if (this.revisionOperationInFlight || this.fileEditorState().busy) {
      return false;
    }
    if (!this.fileEditorState().dirty) return true;
    if (this.pendingExternalNavigationUrl === null) {
      this.pendingExternalNavigationUrl = nextUrl;
      this.revisionFileEditor.requestNavigation('EXTERNAL');
    }
    return false;
  }

  private resumeExternalNavigation(destination: string): void {
    const collectionId = this.collectionId;
    const generation = this.collectionOwnershipGeneration;
    this.authorizedNavigationUrl = destination;
    this.revisionOperationInFlight = true;
    void this.router.navigateByUrl(destination).then(
      (navigated) => {
        if (!this.isActiveCollectionOwnership(collectionId, generation)) {
          return;
        }
        if (!navigated) {
          this.authorizedNavigationUrl = null;
        }
        this.releaseRevisionNavigationBarrier();
      },
      (error: unknown) => {
        if (!this.isActiveCollectionOwnership(collectionId, generation)) {
          return;
        }
        this.authorizedNavigationUrl = null;
        this.releaseRevisionNavigationBarrier();
        this.handleApiError(error, this.getUnexpectedErrorMessage());
      },
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): string | void {
    if (!this.fileEditorState().dirty && !this.fileEditorState().busy) return;
    event.preventDefault();
    event.returnValue = '';
    return '';
  }

  private showRevisionComplete(workflow: CollectionWorkflowDto): void {
    this.exactConnectionPollIntent$.next(null);
    this.hideAuthenticating(true);
    this.connectionPollError.set(null);
    this.exactConnectionRuntime.set(null);
    this.connectionScreen.set(null);
    this.clearRedirectTimers();
    this.clearErrorToast();
    this.clearPendingAuthInteractionState();
    this.authInteractionPersistence.clearCollection(this.collectionId);
    this.isSuccess.set(true);
    this.totalFiles.set(0);
    this.resultMessage.set('Collection revision completed successfully!');
    this.errorDetails.set(undefined);
    this.currentStep.set('result');
    this.loadCompletedRevisionFileTotal(
      workflow,
      ++this.completedRevisionFileTotalGeneration,
    );
  }

  private loadCompletedRevisionFileTotal(
    workflow: CollectionWorkflowDto,
    aggregateGeneration: number,
  ): void {
    const completedTargets = [...workflow.targets]
      .filter((target) => target.target_status === 'COMPLETED')
      .sort(
        (left, right) => left.order_idx_snapshot - right.order_idx_snapshot,
      );
    if (completedTargets.length === 0) {
      return;
    }

    const generation = this.collectionOwnershipGeneration;
    forkJoin(
      completedTargets.map((target) =>
        this.collectionRevisionService
          .getTargetFileSet(
            workflow.collection_id,
            workflow.revision_id,
            target.target_id,
          )
          .pipe(
            map(({ targetFileSet }) => {
              if (
                targetFileSet.collection_id !== workflow.collection_id ||
                targetFileSet.revision_id !== workflow.revision_id ||
                targetFileSet.target_id !== target.target_id
              ) {
                throw new Error(
                  'Completed revision file-set response identity mismatch.',
                );
              }
              return targetFileSet.files.length;
            }),
          ),
      ),
    )
      .pipe(
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (fileCounts) => {
          if (
            !this.isActiveCollectionOwnership(
              workflow.collection_id,
              generation,
            ) ||
            this.completedRevisionFileTotalGeneration !== aggregateGeneration ||
            this.currentStep() !== 'result'
          ) {
            return;
          }
          this.totalFiles.set(
            fileCounts.reduce((total, fileCount) => total + fileCount, 0),
          );
        },
        error: (error) => {
          if (
            this.isActiveCollectionOwnership(
              workflow.collection_id,
              generation,
            ) &&
            this.completedRevisionFileTotalGeneration === aggregateGeneration &&
            this.currentStep() === 'result'
          ) {
            this.handleApiError(error, this.getUnexpectedErrorMessage());
          }
        },
      });
  }

  // Share flow handlers
  onShareClick(): void {
    this.showTargetSelectionDialog.set(true);
  }

  onTargetSelectionConfirm(targetIds: string[]): void {
    this.shareSubmitting.set(true);

    // Calculate expires_at (7 days from now)
    const expiresAt = new Date(
      Date.now() + 7 * 24 * 60 * 60 * 1000,
    ).toISOString();

    const workflow = this.workflow();
    if (!workflow) {
      this.shareSubmitting.set(false);
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }

    this.collectionService
      .createRevisionShare(this.collectionId, workflow.revision_id, {
        target_ids: targetIds,
        expires_at: expiresAt,
      })
      .pipe(
        finalize(() => this.shareSubmitting.set(false)),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.shareResponse.set(response);
          this.showTargetSelectionDialog.set(false);
          this.showShareInfoDialog.set(true);
        },
        error: (error) => {
          console.error('Error sharing targets:', error);
          this.showToast(this.getUnexpectedErrorMessage());
        },
      });
  }

  onTargetSelectionCancel(): void {
    this.showTargetSelectionDialog.set(false);
    this.shareSubmitting.set(false);
  }

  onShareInfoClose(): void {
    this.showShareInfoDialog.set(false);
    this.showSkipConfirmationDialog.set(true);
    this.shareSubmitting.set(false);
  }

  onSkipConfirm(): void {
    this.showSkipConfirmationDialog.set(false);
    this.loadTargetShares();
  }

  onSkipCancel(): void {
    this.showSkipConfirmationDialog.set(false);
    // User chooses to continue with the current step, do nothing
  }

  onPendingSharesClose(): void {
    window.close();
  }

  onPendingSharesBack(): void {
    const identity = this.getRevisionRouteIdentity();
    if (identity) this.replayTargetRoute(identity);
  }

  onShareRevokeRequested(shareId: string): void {
    const workflow = this.workflow();
    if (!workflow || this.pendingSharesLoading()) return;
    this.pendingSharesLoading.set(true);
    this.collectionService
      .revokeRevisionShare(this.collectionId, workflow.revision_id, shareId)
      .pipe(
        switchMap(() =>
          this.collectionService.getRevisionTargetShares(
            this.collectionId,
            workflow.revision_id,
          ),
        ),
        finalize(() => this.pendingSharesLoading.set(false)),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (shares) => {
          this.targetShares.set(shares);
          this.pendingSharesStep?.onShareInfoClose();
          this.pendingSharesStep?.showSuccessToast();
          if (shares.length === 0) {
            this.onAllSharesRevoked();
          }
        },
        error: (error) => {
          console.error('Error revoking target share:', error);
          this.showToast(this.getUnexpectedErrorMessage());
        },
      });
  }

  onAllSharesRevoked(): void {
    // Hide the skip button
    this.skipButtonHidden.set(true);

    // Show alert toast
    this.toastMessage.set(
      this.translocoService.translate(
        'all_shares_revoked_alert',
        {},
        'mzic-collection-flow',
      ),
    );
    this.toastState.set('alert');
    this.toastVisible.set(true);

    const identity = this.getRevisionRouteIdentity();
    if (identity) this.replayTargetRoute(identity);
  }

  onFileUploadSkip(): void {
    // Go directly to pending-shares (user has already shared previously)
    this.loadTargetShares();
  }

  private loadTargetShares(): void {
    const workflow = this.workflow();
    if (!workflow) {
      this.showToast(this.getUnexpectedErrorMessage());
      return;
    }
    this.pendingSharesLoading.set(true);
    this.currentStep.set('pending-shares');

    this.collectionService
      .getRevisionTargetShares(this.collectionId, workflow.revision_id)
      .pipe(
        finalize(() => {
          this.pendingSharesLoading.set(false);
        }),
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (shares) => {
          this.targetShares.set(shares);
        },
        error: (error) => {
          console.error('Error loading target shares:', error);
          // Still show the screen even if loading fails
          this.targetShares.set([]);
          this.showToast(this.getUnexpectedErrorMessage());
        },
      });
  }

  get shareUrl(): string {
    const baseUrl = this.collectionService.getApiUrl().replace('/api', '');
    return `${baseUrl}/collection/${this.collectionId}`;
  }

  private clearRevisionShareState(): void {
    this.showTargetSelectionDialog.set(false);
    this.showShareInfoDialog.set(false);
    this.showSkipConfirmationDialog.set(false);
    this.shareResponse.set(null);
    this.targetShares.set([]);
    this.pendingSharesLoading.set(false);
    this.shareSubmitting.set(false);
  }

  private refreshRevisionWorkflowAndRoute(
    releaseNavigationBarrier = false,
  ): void {
    this.collectionRevisionService
      .getWorkflow(this.collectionId)
      .pipe(
        takeUntil(this.stopCollectionOwnership$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (workflow) => {
          if (isRevisionLifecycleTerminal(workflow)) {
            this.showRevisionComplete(workflow);
            if (releaseNavigationBarrier) {
              this.releaseRevisionNavigationBarrier();
            }
            return;
          }
          const nextTarget = [...workflow.targets]
            .sort(
              (left, right) =>
                left.order_idx_snapshot - right.order_idx_snapshot,
            )
            .find((target) => target.target_status !== 'COMPLETED');
          if (nextTarget) {
            const destination = {
              collectionId: workflow.collection_id,
              revisionId: workflow.revision_id,
              targetId: nextTarget.target_id,
            };
            this.setCanonicalWorkflowRouteHandoff(destination, workflow);
            const navigation = this.navigateToRevisionTarget(destination);
            if (releaseNavigationBarrier) {
              this.settleRevisionRouteNavigation(navigation);
            }
            return;
          }
          this.showToast(
            this.translocoService.translate(
              'collection_revision_awaiting_lifecycle',
              {},
              'mzic-collection-flow',
            ),
            'info',
          );
          if (releaseNavigationBarrier) {
            this.releaseRevisionNavigationBarrier();
          }
        },
        error: (error) => {
          if (releaseNavigationBarrier) {
            this.releaseRevisionNavigationBarrier();
          }
          this.handleApiError(error, this.getUnexpectedErrorMessage());
        },
      });
  }

  private startRedirectCountdown(redirectUrl: string): void {
    this.clearRedirectTimers();
    this.redirectScheduled = true;

    let secondsRemaining = this.COMPLETE_REDIRECT_DELAY_SECONDS;
    this.showToast(this.getRedirectCountdownMessage(secondsRemaining), 'info');

    this.redirectTimeoutId = window.setTimeout(() => {
      if (!this.redirectScheduled) return;
      this.clearRedirectTimers();
      window.location.assign(redirectUrl);
    }, this.COMPLETE_REDIRECT_DELAY_SECONDS * 1000);

    if (this.COMPLETE_REDIRECT_DELAY_SECONDS <= 0) {
      return;
    }

    this.redirectIntervalId = window.setInterval(() => {
      if (!this.redirectScheduled) return;

      secondsRemaining = Math.max(0, secondsRemaining - 1);
      if (secondsRemaining === 0) {
        return;
      }

      this.showToast(
        this.getRedirectCountdownMessage(secondsRemaining),
        'info',
      );
    }, 1000);
  }

  private clearRedirectTimers(): void {
    if (this.redirectTimeoutId !== null) {
      window.clearTimeout(this.redirectTimeoutId);
      this.redirectTimeoutId = null;
    }

    if (this.redirectIntervalId !== null) {
      window.clearInterval(this.redirectIntervalId);
      this.redirectIntervalId = null;
    }

    this.redirectScheduled = false;
    if (this.toastState() === 'info') {
      this.toastVisible.set(false);
    }
  }

  private getRedirectCountdownMessage(seconds: number): string {
    const fallback =
      seconds === 1
        ? 'You will be redirected in 1 second.'
        : `You will be redirected in ${seconds} seconds.`;

    const translated = this.translocoService.translate(
      'redirect_countdown_message',
      { seconds },
      'mzic-collection-flow',
    );

    if (
      !translated ||
      translated === 'redirect_countdown_message' ||
      translated.includes('redirect_countdown_message')
    ) {
      return fallback;
    }

    return translated;
  }

  /**
   * Handles errors during credential submission without navigating away.
   * Shows an error toast and stays on the credentials step for retry.
   * Supports both new structured error format and legacy formats.
   */
  private handleCredentialsError(error: unknown): void {
    const responseError = error as {
      status?: number;
      error?: { message?: string };
    };
    const parsedError = parseApiError(error);

    if (parsedError) {
      let errorMessage = '';

      switch (parsedError.code) {
        case 'INVALID_CREDENTIALS':
          // Use detailed reason if available, otherwise use generic message
          if (parsedError.details?.reason) {
            errorMessage = buildErrorMessage(parsedError);
          } else {
            const fallback =
              'The username or password is incorrect. Please verify and try again.';
            errorMessage = buildErrorMessage(
              parsedError,
              this.translocoService.translate(
                'invalid_credentials_error',
                {},
                'mzic-collection-flow',
              ) || fallback,
            );
          }
          break;

        case 'CONNECTION_FAILED':
          if (parsedError.details?.reason) {
            errorMessage = buildErrorMessage(parsedError);
          } else {
            const fallback =
              'Could not connect to the provider. Please try again later.';
            errorMessage = buildErrorMessage(
              parsedError,
              this.translocoService.translate(
                'connection_failed_error',
                {},
                'mzic-collection-flow',
              ) || fallback,
            );
          }
          break;

        case PROVIDER_SETUP_REQUIRED_CODE:
          // Its own screen explains the provider-side setup.
          this.currentStep.set('provider-setup-required');
          return;

        case 'COLLECTION_EXPIRED':
          errorMessage = buildErrorMessage(
            parsedError,
            this.translocoService.translate(
              'collection_expired_error',
              {},
              'mzic-collection-flow',
            ) || 'This collection link has expired.',
          );
          break;

        case 'COLLECTION_CANCELLED':
          errorMessage = buildErrorMessage(
            parsedError,
            this.translocoService.translate(
              'collection_cancelled_error',
              {},
              'mzic-collection-flow',
            ) || 'This collection has been cancelled.',
          );
          break;

        default:
          errorMessage = buildErrorMessage(parsedError);
      }

      this.showToast(errorMessage);
      return;
    }

    // Fallback for unparseable errors
    let errorMessage = '';

    if (responseError.status === 401) {
      errorMessage =
        this.translocoService.translate(
          'invalid_credentials_error',
          {},
          'mzic-collection-flow',
        ) || 'Invalid credentials. Please check your username and password.';
    } else if (responseError.status === 0) {
      errorMessage =
        this.translocoService.translate(
          'network_error',
          {},
          'mzic-collection-flow',
        ) ||
        'Could not connect to the server. Please check your internet connection.';
    } else {
      errorMessage =
        responseError.error?.message || this.getUnexpectedErrorMessage();
    }

    this.showToast(errorMessage);
    // Stay on credentials step for retry
  }
  private handleApiError(error: unknown, defaultMessage: string): void {
    console.error('API Error:', error);

    if (error instanceof CollectionWebLocksUnavailableError) {
      this.showToast(
        this.translocoService.translate(
          'collection_web_locks_unavailable',
          {},
          'mzic-collection-flow',
        ),
      );
      return;
    }

    // Try to parse structured error
    const parsedError = parseApiError(error);
    if (parsedError) {
      const errorMessage = buildErrorMessage(parsedError);
      this.showToast(errorMessage);
      return;
    }

    // Fallback to default message
    this.showToast(defaultMessage || this.getUnexpectedErrorMessage());
  }

  private showErrorScreen(title: string, description: string): void {
    this.showToast(description || title);
  }

  private showToast(
    message: string,
    state: 'error' | 'success' | 'alert' | 'info' = 'error',
  ): void {
    this.toastMessage.set(message);
    this.toastState.set(state);
    this.toastVisible.set(true);
  }

  private clearErrorToast(): void {
    if (!this.toastVisible() || this.toastState() !== 'error') return;
    this.toastVisible.set(false);
    this.toastMessage.set('');
  }

  private getUnexpectedErrorMessage(): string {
    const scope = 'mzic-collection-flow';
    const key = 'error_unexpected';

    // Try scoped translation first
    const translated = this.translocoService.translate(key, {}, scope);

    // Transloco may return the key or a composed key when the scope hasn't
    // been loaded yet (e.g. "mzicCollectionFlow.error_unexpected"). In that
    // case fall back to a safe generic message in the active language.
    if (
      !translated ||
      translated === key ||
      translated.includes('error_unexpected') ||
      translated.includes(scope.replace(/-/g, ''))
    ) {
      const active = this.translocoService.getActiveLang();
      return active === 'pt-br'
        ? 'Um erro inesperado ocorreu. Por favor tente novamente.'
        : 'An unexpected error occurred. Please try again.';
    }

    return translated;
  }

  private getInvalidCollectionMessage(): string {
    const scope = 'mzic-collection-flow';
    const key = 'invalid_collection_id';

    try {
      const translated = this.translocoService.translate(key, {}, scope);
      if (
        translated &&
        translated !== key &&
        !translated.includes(key) &&
        !translated.includes(scope.replace(/-/g, ''))
      ) {
        return translated;
      }
    } catch (e) {
      // ignore and fall through to fallback
    }

    const active = this.translocoService.getActiveLang();
    return active === 'pt-br'
      ? 'ID da coleta inválido'
      : 'Invalid collection ID';
  }

  // Result step handlers
  onResultRetry(): void {
    this.clearRedirectTimers();
    this.isSuccess.set(false);
    this.resultMessage.set('');
    this.errorDetails.set(undefined);
    this.currentStep.set('credentials');
  }

  onResultClose(): void {
    this.clearRedirectTimers();
    window.close();
  }

  onLongWaitDialogAcknowledged(): void {
    this.longWaitDialogState.set('acknowledged');
  }

  // Authenticating overlay helpers
  private startLongWaitDialogTimer(): void {
    this.resetLongWaitDialogState();

    this.longWaitDialogTimeoutId = window.setTimeout(() => {
      const status = this.authenticatingStatus();
      if (
        this.authenticating() &&
        (status === 'CONNECTING' || status === 'IN_PROGRESS') &&
        this.longWaitDialogState() === 'hidden'
      ) {
        this.longWaitDialogState.set('visible');
      }
    }, this.AUTHENTICATING_LONG_WAIT_DIALOG_MS);
  }

  private resetLongWaitDialogState(): void {
    if (this.longWaitDialogTimeoutId !== null) {
      window.clearTimeout(this.longWaitDialogTimeoutId);
      this.longWaitDialogTimeoutId = null;
    }

    this.longWaitDialogState.set('hidden');
  }

  private showAuthenticating(): void {
    this.authenticatingStartTime = Date.now();
    this.authenticatingStatus.set('CONNECTING');
    this.authenticating.set(true);
    this.startLongWaitDialogTimer();
  }

  private hideAuthenticating(force = false): void {
    if (force) {
      this.authenticating.set(false);
      this.authenticatingStartTime = null;
      this.resetLongWaitDialogState();
      return;
    }

    if (!this.authenticatingStartTime) {
      this.authenticating.set(false);
      this.resetLongWaitDialogState();
      return;
    }

    const elapsed = Date.now() - this.authenticatingStartTime;
    const remaining = this.AUTHENTICATING_MIN_DISPLAY_MS - elapsed;

    if (remaining <= 0) {
      this.authenticating.set(false);
      this.authenticatingStartTime = null;
      this.resetLongWaitDialogState();
    } else {
      // Wait for remaining time before hiding
      timer(remaining)
        .pipe(
          takeUntil(this.stopCollectionOwnership$),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.authenticating.set(false);
          this.authenticatingStartTime = null;
          this.resetLongWaitDialogState();
        });
    }
  }

  private lockDocumentScroll(): void {
    if (this.documentScrollLocked) {
      return;
    }

    this.originalBodyOverflow = this.document.body.style.overflow;
    this.originalHtmlOverflow = this.document.documentElement.style.overflow;
    this.document.body.style.overflow = 'hidden';
    this.document.documentElement.style.overflow = 'hidden';
    this.documentScrollLocked = true;
  }

  private restoreDocumentScroll(): void {
    if (!this.documentScrollLocked) {
      return;
    }

    this.document.body.style.overflow = this.originalBodyOverflow;
    this.document.documentElement.style.overflow = this.originalHtmlOverflow;
    this.documentScrollLocked = false;
  }

  get currentTargetInfo() {
    const target = this.loadedTarget();
    return target
      ? {
          targetId: target.target_id,
          revenueType: target.revenue_type,
          revenueSubtype: target.revenue_subtype,
        }
      : null;
  }

  /**
   * Checks if the current target has been shared previously.
   * Used to determine if "PULAR ETAPA" button should be shown.
   */
  get hasSharedCurrentTarget(): boolean {
    const currentTarget = this.loadedTarget();
    if (!currentTarget) return false;

    const shares = this.targetShares();
    return shares.some((share) =>
      share.target_ids.includes(currentTarget.target_id),
    );
  }

  get collectionAlias(): string {
    return this.collection()?.alias || 'User';
  }

  get partnerName(): string {
    return this.collection()?.partner?.display_name || 'Nas Nuvens';
  }

  get partnerIconUrl(): string | null {
    return this.collection()?.partner?.icon_url || null;
  }

  get isIntegrationEnabled(): boolean {
    return !!this.collection()?.integration?.enabled;
  }

  get integrationEntityName(): string {
    return (
      this.selectedEntity()?.display_name || this.integrationEntityNameValue()
    );
  }

  get selectedEntityName(): string {
    const selectedName = this.selectedEntity()?.display_name;
    if (selectedName) {
      return selectedName;
    }

    if (this.isIntegrationEnabled) {
      return this.integrationEntityName;
    }

    return '';
  }

  get selectedEntityIconUrl(): string | null {
    return this.selectedEntity()?.icon_url || null;
  }

  get activeLang(): string {
    return this.translocoService.getActiveLang() || 'en';
  }

  get currentFailurePresentation(): {
    message: string;
    code: string | null;
    occurredAt: string | null;
  } | null {
    const lastError = this.exactConnectionRuntime()?.connection?.last_error;
    // The setup screen already explained it; the backend text is not shown.
    if (lastError?.code === PROVIDER_SETUP_REQUIRED_CODE) return null;
    if (lastError) {
      return {
        message: lastError.message,
        code: lastError.code || null,
        occurredAt: null,
      };
    }
    const failure = this.currentTargetFailure();
    return failure ? this.getFailurePresentation(failure) : null;
  }

  private setCurrentConnectionId(connectionId: string | null): void {
    const previousConnectionId = this.currentConnectionId();

    if (previousConnectionId && previousConnectionId !== connectionId) {
      this.collectionRequestIdempotency.clearSubmitAuthInteractionIntents(
        this.collectionId,
        previousConnectionId,
      );
    }

    this.currentConnectionId.set(connectionId);
  }

  get businessName(): string {
    return (
      this.businessEntity()?.trade_name ||
      this.businessEntity()?.legal_name ||
      ''
    );
  }
}

function requireCollectionWorkflowIdentity(
  collection: CollectionEnvelopeDto,
  workflow: CollectionWorkflowDto,
  collectionId: string,
): void {
  if (
    collection.id !== collectionId ||
    workflow.collection_id !== collectionId ||
    workflow.revision_id.trim().length === 0
  ) {
    throw new Error(
      'Collection envelope and workflow do not identify the requested collection.',
    );
  }
}

function isRevisionLifecycleTerminal(workflow: CollectionWorkflowDto): boolean {
  return (
    workflow.revision_status === 'SEALED' ||
    workflow.revision_status === 'SUBMITTED' ||
    workflow.revision_status === 'CANCELLED'
  );
}

/**
 * Typed outcome (HTTP 410) of detail, submit, resend and push checks on an
 * interaction that already ended; the connection target says how.
 */
function isEndedAuthInteractionError(error: unknown): boolean {
  const code = parseApiError(error)?.code;
  return (
    code === 'AUTH_INTERACTION_EXPIRED' ||
    code === 'AUTH_INTERACTION_REJECTED' ||
    code === 'AUTH_INTERACTION_FAILED'
  );
}

/** Same pending prompt, whichever object carries its identity. */
function sameAuthInteractionPrompt(
  left: CollectionRevisionAuthInteractionIdentity | null,
  right: CollectionRevisionAuthInteractionIdentity | null,
): boolean {
  return (
    left === right ||
    (!!left &&
      !!right &&
      left.challengeId === right.challengeId &&
      left.generation === right.generation &&
      left.kind === right.kind)
  );
}

function isUnauthorizedRouteError(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  return status === 401 || status === 403;
}

function toConnectionEntityView(entity: ConnectionEntityDto): ConnectionEntity {
  return {
    id: entity.id,
    name: entity.name,
    display_name: entity.display_name,
    icon_url: entity.icon_url,
    description: entity.description,
    credential_schema:
      entity.credential_schema as unknown as ConnectionEntity['credential_schema'],
    auth_capabilities: [...entity.auth_capabilities],
    active: true,
    require_waiver: entity.require_waiver,
  };
}

/**
 * Runtime values win (D3); missing ones fall back to the entity capability
 * declared for the same kind, then to `UNKNOWN` / free length.
 */
function toAuthInteractionView(
  detail: CollectionRevisionAuthInteractionDetailDto,
  authCapabilities: readonly Readonly<Record<string, unknown>>[],
): AuthInteraction {
  const capability =
    authCapabilities.find((entry) => entry['kind'] === detail.kind) ?? null;
  return {
    kind: detail.kind,
    channel:
      toAuthInteractionChannel(detail.channel) ??
      toAuthInteractionChannel(capability?.['channel']) ??
      'UNKNOWN',
    code_format:
      toAuthInteractionCodeFormat(detail.code_format) ??
      toAuthInteractionCodeFormat(capability?.['code_format']),
    destination_hint: detail.destination_hint?.trim() || null,
    authenticator_label: detail.authenticator_label?.trim() || null,
    resend_available_at: detail.resend_available_at ?? null,
    last_error: toAuthInteractionLastError(detail.last_error),
    challenge_number: detail.challenge_number?.trim() || null,
    expires_at: detail.expires_at,
    payload_schema: detail.payload_schema ? { ...detail.payload_schema } : null,
    resend_supported: detail.resend_supported,
    page: detail.page
      ? {
          page: detail.page.page,
          size: detail.page.size,
          total_items: detail.page.total_items,
          total_pages: detail.page.total_pages,
          items: detail.page.items.map((item) => ({
            id: item.id,
            label: item.label,
            description: item.description,
            metadata: { ...item.metadata },
          })),
        }
      : null,
    metadata: { ...detail.metadata },
  };
}

function toAuthInteractionChannel(
  value: unknown,
): Exclude<AuthInteractionChannel, 'UNKNOWN'> | null {
  return value === 'EMAIL' || value === 'SMS' || value === 'AUTHENTICATOR_APP'
    ? value
    : null;
}

function toAuthInteractionCodeFormat(
  value: unknown,
): AuthInteractionCodeFormat | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { charset, length } = value as Record<string, unknown>;
  return {
    charset: charset === 'NUMERIC' ? 'NUMERIC' : 'ALPHANUMERIC',
    length:
      typeof length === 'number' && Number.isInteger(length) && length > 0
        ? length
        : null,
  };
}

function toAuthInteractionLastError(
  value: CollectionRevisionAuthInteractionDetailDto['last_error'],
): AuthInteractionLastError | null {
  if (value?.code !== 'INVALID_CODE' && value?.code !== 'TOO_MANY_ATTEMPTS') {
    return null;
  }
  return {
    code: value.code,
    retry_after_seconds: value.retry_after_seconds ?? null,
    retry_available_at: value.retry_available_at ?? null,
  };
}

/**
 * Which two-factor screen an ended verification shows: only a push can be
 * declined; otherwise the kind of the prompt that ended in this session.
 * `null` when unknown (e.g. after a reload): providers may declare both code
 * and push, so the capabilities cannot tell, and the screen stays neutral.
 */
function endedTwoFactorKind(
  reason: AuthInteractionEndedReason,
  endedKind: AuthInteractionKind | null,
): 'TWO_FA_CODE' | 'PUSH_APPROVAL' | null {
  if (reason === 'REJECTED') return 'PUSH_APPROVAL';
  return endedKind === 'TWO_FA_CODE' || endedKind === 'PUSH_APPROVAL'
    ? endedKind
    : null;
}

/**
 * The two-factor screen of a verification that already ended: no prompt is
 * pending, so the channel comes from the capability of the known kind. An
 * unknown kind renders the neutral screen (generic icon and wording).
 */
function toEndedTwoFactorView(
  authCapabilities: readonly Readonly<Record<string, unknown>>[],
  kind: 'TWO_FA_CODE' | 'PUSH_APPROVAL' | null,
): AuthInteraction {
  const capability = kind
    ? (authCapabilities.find((entry) => entry['kind'] === kind) ?? null)
    : null;
  return {
    kind: kind ?? 'TWO_FA_CODE',
    channel: toAuthInteractionChannel(capability?.['channel']) ?? 'UNKNOWN',
    code_format: toAuthInteractionCodeFormat(capability?.['code_format']),
    destination_hint: null,
    authenticator_label: null,
    resend_available_at: null,
    last_error: null,
    challenge_number: null,
    expires_at: null,
    payload_schema: null,
    resend_supported: false,
    page: null,
    metadata: {},
  };
}

function toWaiverDocumentView(
  document: EntityOwnerWaiverDocumentDto,
  validityDays: number | null,
): WaiverDocument {
  return {
    id: document.operation_id,
    operation_id: document.operation_id,
    operation_type: document.operation_type,
    document_url: document.document_url ?? '',
    generated_at: document.generated_at,
    signed_at: document.signed_at,
    expires_at: document.expires_at ?? '',
    validity_days: validityDays ?? 0,
  };
}

function isDefinitiveConnectFailure(error: unknown): boolean {
  const status = (error as { readonly status?: number } | null)?.status;
  return (
    status !== 0 &&
    status !== 408 &&
    !(typeof status === 'number' && status >= 500)
  );
}

function createConnectionCandidateId(): string {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (!randomUUID) {
    throw new Error(
      'Creating a connection requires crypto.randomUUID support.',
    );
  }
  return randomUUID.call(globalThis.crypto);
}

function toExactAuthInteractionIdentity(
  runtime: CollectionRevisionConnectionTargetDto,
): CollectionRevisionAuthInteractionIdentity | undefined {
  const connection = runtime.connection;
  const interaction = connection?.auth_interaction;
  if (!connection || !interaction) return undefined;
  return {
    collectionId: runtime.collection_id,
    revisionId: runtime.revision_id,
    targetId: runtime.target_id,
    connectionId: connection.connection_id,
    challengeId: interaction.challenge_id,
    generation: interaction.generation,
    kind: interaction.kind,
  };
}

function sameRevisionTargetIdentity(
  left: CollectionRevisionTargetRouteIdentity,
  right: CollectionRevisionTargetRouteIdentity,
): boolean {
  return (
    left.collectionId === right.collectionId &&
    left.revisionId === right.revisionId &&
    left.targetId === right.targetId
  );
}
